/**
 * Design Radio Selector v3.14 - COLLECTION ONLY ARCHITECTURE
 *
 * Uses ONLY the Collection Library for both thumbnails AND page loading.
 * No individual designIds needed - everything uses collection page numbers.
 *
 * KEY DIFFERENCES FROM v3.13:
 *   - NO load-design-by-id for front page
 *   - Front page uses Page Loader SWAP (like back page uses ADD)
 *   - Both front and back use collectionPage number (1-15)
 *   - Thumbnails use collection preview URLs directly
 *
 * FLOW - SINGLE SIDED:
 *   1. User selects design (collectionPage)
 *   2. Show overlay, open editor behind it
 *   3. Wait for editor-shown
 *   4. Use SwapController.swapPage(pageIndex, 0) to swap page 0 to the selected design
 *   5. Wait for page-render-complete
 *   6. Save via SwapController.saveProject()
 *   7. Close editor, hide overlay
 *
 * FLOW - DOUBLE SIDED:
 *   1. User selects front design (collectionPage)
 *   2. User selects back design (collectionPage)
 *   3. Show overlay, open editor behind it
 *   4. Swap page 0 to front design (creates 2-page design automatically)
 *   5. Swap page 1 to back design (NOT add - design already has 2 pages)
 *   6. Save
 *   7. Close editor, hide overlay
 *
 * NOTE: After step 4, PitchPrint fires app-client-pageCount 2, so model.limitReached
 * becomes TRUE. This prevents ADD from working, so we use SWAP for both pages.
 *
 * BENEFITS:
 *   - Single source of truth (Collection Library)
 *   - Updates in PitchPrint Admin flow automatically
 *   - No manual designId mapping needed
 *   - Simpler metafield format (just collectionPage and title)
 *
 * TRADE-OFF:
 *   - Slightly slower (~3-5s overhead) because front page needs swap instead of direct load
 *   - v3.13: ~5-8s single, ~10-15s double
 *   - v3.14: ~8-12s single, ~12-18s double
 */

(function() {
  'use strict';

  var DEBUG = false;
  var log = function() {
    if (DEBUG || window.PITCHPRINT_DEBUG) {
      console.log.apply(console, ['[DesignRadio v3.14]'].concat(Array.prototype.slice.call(arguments)));
    }
  };

  function DesignRadioSelector() {
    this.container = null;
    this.statusEl = null;
    this.selectedTemplate = null;
    this.isLoading = false;
    this.config = window.designRadioConfig || {};
    this.ppclientReady = false;
    this.pendingSelection = null;
    this.currentShape = null;

    // Double-sided selection tracking
    this.frontSelection = null;
    this.backSelection = null;
    this.isDoubleSidedMode = false;

    // v3.15.75: Per-side customization state (replaces global toggle states)
    // v4.3.16: logoToggleEnabled defaults to false - user must explicitly enable to upload logo
    // v4.3.25: textToggleEnabled also defaults to false - opt-in for both toggles (matches HTML checkbox state)
    this.sideCustomizations = {
      front: {
        textToggleEnabled: false,
        logoToggleEnabled: false,
        backgroundColor: null,
        backgroundCMYK: null,
        logoUrl: null,
        textValue: ''
      },
      back: {
        textToggleEnabled: false,
        logoToggleEnabled: false,
        backgroundColor: null,
        backgroundCMYK: null,
        logoUrl: null,
        textValue: ''
      }
    };

    // v3.15.47: Legacy toggle states (kept for backward compatibility, but now just references)
    // These are now computed from sideCustomizations.front for single-sided products
    // v4.3.25: textToggleEnabled also defaults to false
    this.textToggleEnabled = false;
    this.logoToggleEnabled = false;

    // v3.15.51: Pending customizations for double-sided flow
    this._pendingDoubleSidedCustomizations = null;

    // v3.15.55: CMYK color support (legacy - now in sideCustomizations)
    this.selectedBackgroundCMYK = null;
    // v3.15.58: iro.js color picker instance
    this.colorPickerInstance = null;

    this.init();
  }

  // Shape normalization map
  var SHAPE_NORMALIZATION = {
    'classic shape': 'classic',
    'classic': 'classic',
    'circle shape': 'circle',
    'circle': 'circle',
    'longboard shape': 'longboard',
    'longboard': 'longboard'
  };

  // v3.15.75: Helper to get side ('front' or 'back') from an element
  function getSideFromElement(element) {
    if (!element) return 'front';
    // Check data-side attribute first
    if (element.dataset && element.dataset.side) {
      return element.dataset.side;
    }
    // Check if element ID contains -front or -back
    if (element.id) {
      if (element.id.indexOf('-front') !== -1) return 'front';
      if (element.id.indexOf('-back') !== -1) return 'back';
    }
    // Check parent panel
    var panel = element.closest('.design-customizer');
    if (panel) {
      if (panel.id === 'design-customizer-front') return 'front';
      if (panel.id === 'design-customizer-back') return 'back';
    }
    // Default to front for backward compatibility
    return 'front';
  }

  DesignRadioSelector.prototype.init = function() {
    var self = this;

    this.container = document.getElementById('design-radio-selector');
    if (!this.container) {
      log('Container not found, skipping initialization');
      return;
    }

    // Check if this is actually v3.14
    var version = this.container.dataset.version;
    if (version !== '3.14') {
      log('Not v3.14 container (version=' + version + '), skipping');
      return;
    }

    this.statusEl = document.getElementById('design-radio-status-front');

    log('Initializing design radio selector v3.14 (Collection-Only)');
    log('Config:', this.config);

    this.setStatus('initializing', 'Initializing...');

    // v4.4.2: Set up variant change listeners IMMEDIATELY (don't wait for PitchPrint)
    // This prevents race condition with card-variants applying popular variant
    this.setupVariantChangeListener();
    // v4.4.2: Check variant state immediately on init
    this.checkDoubleSidedVariant();
    this.detectAndFilterByShape();

    this.waitForPitchPrint().then(function() {
      self.ppclientReady = true;
      self.setupEventListeners();
      self.setupPitchPrintListeners();
      self.setupCustomizationUI();
      // v4.7.0: Auto-select AFTER customization UI is set up (prevents hideCustomizationPanel override)
      self.autoSelectDesignForSplitProduct();
      self.setStatus('ready', 'Select a design to continue');
      log('Design radio selector v3.14 ready');

      if (self.pendingSelection) {
        log('Processing pending selection');
        self.loadDesignViaSwap(self.pendingSelection);
        self.pendingSelection = null;
      }
    }).catch(function(err) {
      log('PitchPrint client not available:', err);
      self.setupEventListeners();
      // v3.15.31: Always setup customization UI - it doesn't require PitchPrint
      self.setupCustomizationUI();
      // v4.7.0: Auto-select AFTER customization UI is set up (prevents hideCustomizationPanel override)
      self.autoSelectDesignForSplitProduct();
      self.setStatus('ready', 'Select a design to continue');
    });
  };

  DesignRadioSelector.prototype.waitForPitchPrint = function() {
    var self = this;
    return new Promise(function(resolve, reject) {
      var attempts = 0;
      // v3.15.32: Increased timeout from 10s to 20s for post-reload scenarios
      var maxAttempts = 200;

      var check = setInterval(function() {
        attempts++;

        if (window.ppclient && typeof window.ppclient.fire === 'function') {
          clearInterval(check);
          log('PitchPrint client ready');
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(check);
          log('v3.15.32: PitchPrint timeout after 20s - setting up late-init listener');
          // v3.15.32: Set up a late-init listener for when ppclient becomes available
          self.setupLateInitListener();
          reject(new Error('PitchPrint client not available after 20s'));
        }
      }, 100);
    });
  };

  // v3.15.32: Handle late PitchPrint initialization (e.g., after page reload)
  DesignRadioSelector.prototype.setupLateInitListener = function() {
    var self = this;

    // Check every second if ppclient becomes available
    var lateCheck = setInterval(function() {
      if (window.ppclient && typeof window.ppclient.fire === 'function') {
        clearInterval(lateCheck);
        log('v3.15.32: PitchPrint late-initialized - setting up listeners');
        self.ppclientReady = true;
        self.setupPitchPrintListeners();

        // If there's a pending selection, process it now
        if (self.pendingSelection) {
          log('v3.15.32: Processing pending selection after late-init');
          self.loadDesignViaSwap(self.pendingSelection);
          self.pendingSelection = null;
        }
      }
    }, 1000);

    // Stop checking after 60 seconds total
    setTimeout(function() {
      clearInterval(lateCheck);
      log('v3.15.32: Late-init check stopped after 60s');
    }, 60000);
  };

  DesignRadioSelector.prototype.setupPitchPrintListeners = function() {
    var self = this;

    if (!window.ppclient || !window.ppclient.on) {
      log('ppclient.on not available');
      return;
    }

    window.ppclient.on('project-saved', function(data) {
      log('Project saved event:', data);
    });

    window.ppclient.on('design-load-error', function(data) {
      log('Design load error:', data);
      if (self.isLoading) {
        self.onDesignLoadError(data);
      }
    });

    // v4.3.24: Pre-cache default design when SwapAPI is ready for better first-time experience
    // Note: We use SwapController.onReady instead of project-loaded because project-loaded
    // only fires when user actively opens the editor, not on initial page load
    var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();
    if (swapController) {
      swapController.onReady(function() {
        log('v4.3.24: SwapAPI ready - attempting to pre-cache default design');
        // Delay to allow design to fully render in runtime
        setTimeout(function() {
          self.preCacheDefaultDesign();
        }, 1000);
      });
    }

    // v3.15.30: Cancel reload is now handled via postMessage from iframe
    // See pitchprint-swap-controller.js for 'editor-cancelled' handler

    log('PitchPrint event listeners attached');
  };

  DesignRadioSelector.prototype.setupEventListeners = function() {
    var self = this;

    // Front radio buttons
    var frontRadios = this.container.querySelectorAll('input[type="radio"][name="design_template_front"]');
    frontRadios.forEach(function(radio) {
      radio.addEventListener('change', function(e) {
        self.handleTemplateSelection(e.target, 'front');
      });
    });

    // Back radio buttons
    var backRadios = this.container.querySelectorAll('input[type="radio"][name="design_template_back"]');
    backRadios.forEach(function(radio) {
      radio.addEventListener('change', function(e) {
        self.handleTemplateSelection(e.target, 'back');
      });
    });

    // Same as front checkbox
    var sameAsFrontCheckbox = document.getElementById('design-radio-same-as-front');
    if (sameAsFrontCheckbox) {
      sameAsFrontCheckbox.addEventListener('change', function(e) {
        self.handleSameAsFrontChange(e.target.checked);
      });
    }

    // Custom design button
    var customBtn = document.getElementById('design-radio-custom-btn');
    if (customBtn) {
      customBtn.addEventListener('click', function() {
        self.openCustomDesigner();
      });
    }

    // Preview button
    var previewBtn = document.getElementById('design-radio-preview-btn');
    if (previewBtn) {
      previewBtn.addEventListener('click', function(e) {
        e.preventDefault();
        self.handlePreviewButtonClick();
      });
    }

    // v3.15.75: Copy from Front button
    var copyFromFrontBtn = document.getElementById('copy-from-front-btn');
    if (copyFromFrontBtn) {
      copyFromFrontBtn.addEventListener('click', function(e) {
        e.preventDefault();
        self.copyFromFront();
      });
    }

    // Variant change listeners
    this.setupVariantChangeListener();
    this.checkDoubleSidedVariant();
    this.detectAndFilterByShape();

    // v3.15.75: Load saved customizations from localStorage
    this.loadFromLocalStorage();

    // v4.1.6: Track interaction with back section to stop pulse animation
    var backSection = document.getElementById('design-radio-back');
    if (backSection) {
      backSection.addEventListener('click', function() {
        backSection.classList.add('user-interacted');
      }, { once: true });
      backSection.addEventListener('touchstart', function() {
        backSection.classList.add('user-interacted');
      }, { once: true, passive: true });
    }

    log('Event listeners attached');
  };

  // ========== v4.7.0: Auto-Select for Split Child Products ==========

  /**
   * v4.7.0: Auto-select design for split/child products.
   * Called at end of setupEventListeners, after shape filtering.
   * Only selects front design. Does NOT trigger Preview or PitchPrint.
   */
  DesignRadioSelector.prototype.autoSelectDesignForSplitProduct = function() {
    if (!this.config.isSplitChild || !this.config.preSelectedDesign) return;

    var designTitle = this.config.preSelectedDesign.trim();
    if (!designTitle) return;

    log('v4.7.0: Auto-selecting design for split child:', designTitle);

    // Helper: find matching visible radio by name attribute
    var self = this;
    function findMatchingRadio(radioName) {
      var radios = self.container.querySelectorAll(
        'input[type="radio"][name="' + radioName + '"]'
      );
      for (var i = 0; i < radios.length; i++) {
        var radio = radios[i];
        var option = radio.closest('.design-radio-selector__option');
        if (option && option.style.display === 'none') continue;
        // Back radios have " (Back)" suffix in data-title, strip it for comparison
        var radioTitle = (radio.dataset.title || '').trim().replace(/\s*\(Back\)$/i, '').toLowerCase();
        if (radioTitle === designTitle.toLowerCase()) {
          return radio;
        }
      }
      return null;
    }

    // Select front design
    var frontRadio = findMatchingRadio('design_template_front');
    if (!frontRadio) {
      log('v4.7.0: No matching visible front radio for "' + designTitle + '", skipping');
      return;
    }

    frontRadio.checked = true;
    this._skipScrollCount = (this._skipScrollCount || 0) + 1;
    this.handleTemplateSelection(frontRadio, 'front');
    log('v4.7.0: Auto-selected front "' + designTitle + '"');

    // Select back design with same title (if double-sided mode is active)
    if (this.container.classList.contains('is-double-sided')) {
      var backRadio = findMatchingRadio('design_template_back');
      if (backRadio) {
        backRadio.checked = true;
        this._skipScrollCount = (this._skipScrollCount || 0) + 1;
        this.handleTemplateSelection(backRadio, 'back');
        log('v4.7.0: Auto-selected back "' + designTitle + '"');
      } else {
        log('v4.7.0: No matching visible back radio for "' + designTitle + '"');
      }
    }
  };

  // ========== Template Selection ==========

  DesignRadioSelector.prototype.handleTemplateSelection = function(radio, side) {
    if (this.isLoading) {
      log('Load in progress, ignoring selection');
      return;
    }

    side = side || 'front';

    // v3.14: Use collectionPage instead of designId
    var collectionPage = parseInt(radio.dataset.collectionPage, 10);
    var pageIndex = collectionPage - 1; // 0-indexed for SwapController
    var targetPosition = parseInt(radio.dataset.targetPosition || '0', 10);
    var title = radio.dataset.title;
    var previewUrl = radio.dataset.previewUrl;
    var shape = radio.dataset.shape || this.currentShape || 'classic';

    log('Template selected (v3.14 Collection-Only):', {
      collectionPage: collectionPage,
      pageIndex: pageIndex,
      targetPosition: targetPosition,
      side: side,
      title: title,
      shape: shape
    });

    var selection = {
      collectionPage: collectionPage,
      pageIndex: pageIndex,
      targetPosition: targetPosition,
      side: side,
      title: title,
      previewUrl: previewUrl,
      shape: shape,
      radio: radio
    };

    this.isDoubleSidedMode = this.checkIfDoubleSidedActive();

    if (side === 'back') {
      this.backSelection = selection;
      log('Back selection stored:', selection.title);

      // v4.2.0: Notify StateManager of back design selection
      if (window.PitchPrintStateManager && window.PitchPrintStateManager.setQuickDesignBackSelected) {
        window.PitchPrintStateManager.setQuickDesignBackSelected(true);
      }
    } else {
      this.frontSelection = selection;
      this.selectedTemplate = selection;
      log('Front selection stored:', selection.title);

      // v4.2.0: Notify StateManager of front design selection
      if (window.PitchPrintStateManager && window.PitchPrintStateManager.setQuickDesignFrontSelected) {
        window.PitchPrintStateManager.setQuickDesignFrontSelected(true);
      }

      // v4.1.5: Show back section now that front is selected (if double-sided)
      var backSection = document.getElementById('design-radio-back');
      if (backSection && this.container.classList.contains('is-double-sided')) {
        backSection.style.display = '';
        log('v4.1.5: Back section revealed after front selection');
      }

      // v4.1.4: If "same as front" is checked, also update back selection
      var sameAsFrontCheckbox = document.getElementById('design-radio-same-as-front');
      if (sameAsFrontCheckbox && sameAsFrontCheckbox.checked) {
        log('v4.1.4: Same-as-front is checked, syncing back selection');
        this.handleSameAsFrontChange(true);
      }
    }

    this.updateSideSelectionInput(side, collectionPage, title, null);

    var statusId = side === 'back' ? 'design-radio-status-back' : 'design-radio-status-front';
    var statusEl = document.getElementById(statusId) || this.statusEl;

    // Update visual state
    this.setStatusOnElement(statusEl, 'ready', '✓ Selected: ' + title);

    // Update Preview button state (does NOT trigger swap)
    this.updatePreviewButtonState();

    // v3.15.75: Show customization panel for the selected side
    if (this.config.customizationEnabled) {
      this.showCustomizationPanel(side);
    }

    // v4.1.0: Collapse grid after selection (with delay for visual feedback)
    var self = this;
    setTimeout(function() {
      self.collapseGrid(side);
    }, 500);

    if (this.isDoubleSidedMode) {
      log('Double-sided mode active');

      if (this.frontSelection && !this.backSelection) {
        var backStatus = document.getElementById('design-radio-status-back');
        if (backStatus) {
          this.setStatusOnElement(backStatus, 'info', '← Now select a back design');
        }
      } else if (!this.frontSelection && this.backSelection) {
        var frontStatusEl = document.getElementById('design-radio-status-front');
        if (frontStatusEl) {
          this.setStatusOnElement(frontStatusEl, 'info', '← First select a front design');
        }
      }
      // NOTE: Do NOT auto-trigger loadDoubleSidedViaSwap() - wait for Preview button click
    }
    // NOTE: Do NOT auto-trigger loadDesignViaSwap() for single-sided - wait for Preview button click
  };

  // ========== Preview Button Management ==========

  /**
   * Update the Preview button state based on current selections
   * Button shows when selection made, hides after preview loads
   */
  DesignRadioSelector.prototype.updatePreviewButtonState = function() {
    var previewBtn = document.getElementById('design-radio-preview-btn');
    var actionsContainer = document.querySelector('.design-radio-selector__actions');
    if (!previewBtn || !actionsContainer) return;

    // If preview was already loaded, show button again when selection changes
    if (this.previewLoaded) {
      this.previewLoaded = false; // Reset flag - new selection means need to preview again
    }

    var canPreview = false;

    if (this.isDoubleSidedMode) {
      // Double-sided: require both front AND back selections
      canPreview = this.frontSelection && this.backSelection;
    } else {
      // Single-sided: just need front selection
      canPreview = !!this.frontSelection;
    }

    // Show button container, enable/disable button
    actionsContainer.style.display = 'block';
    previewBtn.disabled = !canPreview;

    // Update button text based on state
    var btnText = previewBtn.querySelector('.preview-btn-text');
    if (btnText) {
      if (this.isDoubleSidedMode) {
        if (!this.frontSelection && !this.backSelection) {
          btnText.textContent = 'Select front & back designs';
        } else if (!this.frontSelection) {
          btnText.textContent = 'Select front design';
        } else if (!this.backSelection) {
          btnText.textContent = 'Select back design';
        } else {
          btnText.textContent = 'Preview Design';
        }
      } else {
        btnText.textContent = canPreview ? 'Preview Design' : 'Select a design';
      }
    }

    log('Preview button state updated:', { canPreview: canPreview, isDoubleSided: this.isDoubleSidedMode });
  };

  /**
   * Handle Preview button click - triggers the actual swap operation
   * v3.15.1: ALWAYS swap first, then apply customizations after swap completes
   * This ensures the correct design is loaded before customizations are applied
   */
  DesignRadioSelector.prototype.handlePreviewButtonClick = function() {
    if (this.isLoading) {
      log('Load already in progress');
      return;
    }

    // v4.3.24: If warmup is in progress, close it first - user action takes priority
    if (this._warmupInProgress) {
      log('v4.3.24: Warmup in progress - closing it for user preview');
      this._closeWarmupEditor();
      // Small delay to let editor close before reopening
      var self = this;
      setTimeout(function() {
        self.handlePreviewButtonClick();
      }, 300);
      return;
    }

    // v4.3.26: Retry mechanism for mobile first-preview crashes
    // Clear any existing retry timeout
    if (this._previewRetryTimeout) {
      clearTimeout(this._previewRetryTimeout);
      this._previewRetryTimeout = null;
    }

    // v3.15.47: Validate customizations before proceeding
    var validation = this.validateCustomizations();
    if (!validation.valid) {
      log('v3.15.47: Validation failed:', validation.errors);
      this.showValidationErrors(validation.errors);
      return; // Block preview until validation passes
    }
    // Clear any previous validation errors
    this.clearValidationErrors();

    // v3.15.75: Save text and logo to localStorage for cross-product persistence
    this.saveToLocalStorage();

    // Set button to loading state (in addition to overlay)
    this.setPreviewButtonLoading(true);

    if (this.isDoubleSidedMode) {
      if (this.frontSelection && this.backSelection) {
        log('Preview button clicked - starting double-sided swap');

        // v3.15.51: Gather customizations before double-sided swap
        var customizations = this.gatherCustomizations();
        var hasCustomizations = this.hasCustomizations(customizations);

        if (hasCustomizations) {
          log('v3.15.51: Double-sided preview with customizations:', customizations);
          // Store customizations to apply after BOTH swaps complete
          this._pendingDoubleSidedCustomizations = customizations;
        } else {
          log('v3.15.51: Double-sided preview without customizations');
          this._pendingDoubleSidedCustomizations = null;
        }

        this.loadDoubleSidedViaSwap();
      }
    } else {
      if (this.frontSelection) {
        // v3.15.14 FIX: Check for cached design with title validation
        var customizations = this.gatherCustomizations();
        var hasCustomizations = this.hasCustomizations(customizations);
        var collectionPage = this.frontSelection.collectionPage;
        var shape = this.currentShape || 'classic';
        var templateTitle = this.frontSelection.title;

        // v3.15.14: Pass title to cache lookup for validation (prevents wrong template being returned)
        var cachedDesign = window.DesignTemplateCache && window.DesignTemplateCache.get(collectionPage, shape, templateTitle);

        // v4.4.1: Use direct swap path for mobile (same proven approach as double-sided)
        // Double-sided flow: swap → apply customizations (works on mobile!)
        // Now using same pattern for single-sided on mobile first-preview

        if (cachedDesign && hasCustomizations) {
          // USE CACHED DESIGN PATH - this keeps nav.pages populated so canvas can be found
          log('Preview button clicked - loading customized design from cache for:', templateTitle);
          this._pendingCustomizationsAfterSwap = customizations;
          this.setLoadingState(this.frontSelection.radio, true);
          this.loadCustomizedDesign(cachedDesign, customizations);
        } else {
          // NO CACHE or CACHE MISMATCH - must use swap flow
          if (hasCustomizations) {
            log('Preview button clicked - no valid cache, will swap then apply customizations for:', templateTitle);
            // Store customizations to apply after swap completes
            this._pendingCustomizationsAfterSwap = customizations;
          } else {
            log('Preview button clicked - starting swap for:', templateTitle, '(collectionPage:', collectionPage + ')');
          }

          this.setLoadingState(this.frontSelection.radio, true);

          // v4.4.1: Use direct swap for all platforms (mobile and desktop)
          // Proven approach: swap loads base design → then apply customizations
          // Same pattern as double-sided flow (which works on mobile)
          this.loadDesignViaSwap(this.frontSelection);
        }
      }
    }
  };

  /**
   * Set Preview button to loading or normal state
   */
  DesignRadioSelector.prototype.setPreviewButtonLoading = function(isLoading) {
    var previewBtn = document.getElementById('design-radio-preview-btn');
    if (!previewBtn) return;

    var btnText = previewBtn.querySelector('.preview-btn-text');
    var btnIcon = previewBtn.querySelector('.preview-btn-icon');

    if (isLoading) {
      previewBtn.disabled = true;
      previewBtn.classList.add('is-loading');
      if (btnText) btnText.textContent = 'Loading...';
      if (btnIcon) btnIcon.innerHTML = '<div class="preview-btn-spinner"></div>';
    } else {
      previewBtn.classList.remove('is-loading');
      if (btnIcon) {
        btnIcon.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
      }
    }
  };

  /**
   * Hide the Preview button after successful preview
   * Button will re-appear when user changes selection
   */
  DesignRadioSelector.prototype.hidePreviewButton = function() {
    var actionsContainer = document.querySelector('.design-radio-selector__actions');
    if (actionsContainer) {
      actionsContainer.style.display = 'none';
    }
    this.previewLoaded = true; // Flag to show button again on next selection change
    log('Preview button hidden after successful load');
  };

  // ========== Customization Panel (v3.15) ==========

  /**
   * Initialize customization UI components
   * Sets up event listeners for color swatches, text inputs, and logo upload
   */
  DesignRadioSelector.prototype.setupCustomizationUI = function() {
    var self = this;

    // Check if customization is enabled
    if (!this.config.customizationEnabled) {
      log('Customization not enabled, skipping UI setup');
      return;
    }

    log('Setting up customization UI');

    this.setupColorSwatchListeners();
    this.setupTextInputListeners();
    this.setupLogoUploadListeners();
    this.setupFieldToggleListeners(); // v3.15.47

    // Initially hide the customization panel
    this.hideCustomizationPanel();
  };

  /**
   * v3.15.75: Show the customization panel for a specific side
   * @param {string} side - 'front' or 'back'
   */
  DesignRadioSelector.prototype.showCustomizationPanel = function(side) {
    // v3.15.75: Support per-side panels
    if (side) {
      var panel = document.getElementById('design-customizer-' + side);
      if (panel) {
        panel.style.display = 'block';
        panel.classList.add('is-active');
        log('Customization panel shown for side:', side);
      }
    } else {
      // Legacy: show old shared panel (backward compatibility)
      var panel = document.getElementById('design-customizer');
      if (panel) {
        panel.style.display = 'block';
        panel.classList.add('is-visible');
        log('Customization panel shown (legacy)');
      }
    }
  };

  /**
   * v3.15.75: Hide the customization panel for a specific side
   * @param {string} side - 'front' or 'back', or null for all
   */
  DesignRadioSelector.prototype.hideCustomizationPanel = function(side) {
    // v3.15.75: Support per-side panels
    if (side) {
      var panel = document.getElementById('design-customizer-' + side);
      if (panel) {
        panel.style.display = 'none';
        panel.classList.remove('is-active');
        log('Customization panel hidden for side:', side);
      }
    } else {
      // Hide both panels (or legacy shared panel)
      ['front', 'back'].forEach(function(s) {
        var panel = document.getElementById('design-customizer-' + s);
        if (panel) {
          panel.style.display = 'none';
          panel.classList.remove('is-active');
        }
      });
      // Legacy
      var panel = document.getElementById('design-customizer');
      if (panel) {
        panel.style.display = 'none';
        panel.classList.remove('is-visible');
      }
      log('All customization panels hidden');
    }
  };

  /**
   * Reset all customization inputs to their default state
   */
  DesignRadioSelector.prototype.resetCustomizations = function() {
    // Reset color swatches
    var swatches = document.querySelectorAll('.color-swatch');
    swatches.forEach(function(swatch) {
      swatch.classList.remove('is-selected');
    });
    this.selectedBackgroundColor = null;
    this.selectedBackgroundCMYK = null;  // v3.15.55

    // Reset text inputs
    var textInputs = document.querySelectorAll('.text-customizer__input');
    textInputs.forEach(function(input) {
      input.value = '';
      var field = input.closest('.text-customizer__field');
      if (field) {
        var currentCount = field.querySelector('.text-customizer__char-current');
        if (currentCount) currentCount.textContent = '0';
      }
    });

    // Reset logo upload
    var logoInput = document.getElementById('logo-input');
    if (logoInput) logoInput.value = '';

    var logoPreview = document.getElementById('logo-preview');
    if (logoPreview) {
      logoPreview.style.display = 'none';
      logoPreview.src = '';
    }

    var logoText = document.querySelector('.logo-uploader__text');
    if (logoText) logoText.style.display = '';

    var logoRemove = document.querySelector('.logo-uploader__remove');
    if (logoRemove) logoRemove.style.display = 'none';

    var logoUrlInput = document.getElementById('logo-url');
    if (logoUrlInput) logoUrlInput.value = '';

    this.uploadedLogoUrl = null;

    // v4.3.25: Reset toggles to OFF (opt-in) - matches default state
    var textToggle = document.getElementById('toggle-website-text');
    if (textToggle) {
      textToggle.checked = false;
      this.textToggleEnabled = false;
      this.toggleFieldVisibility('text-customizer-section', false);
    }

    // v4.3.16: Logo toggle defaults to OFF - user must explicitly enable
    var logoToggle = document.getElementById('toggle-logo-upload');
    if (logoToggle) {
      logoToggle.checked = false;
      this.logoToggleEnabled = false;
      this.toggleFieldVisibility('logo-customizer-section', false);
    }

    // v3.15.47: Clear any validation errors
    this.clearValidationErrors();

    log('Customizations reset');
  };

  /**
   * v3.15.47: Set up toggle listeners for optional text/logo fields
   */
  DesignRadioSelector.prototype.setupFieldToggleListeners = function() {
    var self = this;

    // v3.15.75: Handle both front and back toggle elements
    var sides = ['front', 'back'];

    sides.forEach(function(side) {
      // Text toggle for this side
      var textToggle = document.getElementById('toggle-website-text-' + side);
      if (textToggle) {
        textToggle.addEventListener('change', function(e) {
          var toggleSide = getSideFromElement(e.target);
          self.sideCustomizations[toggleSide].textToggleEnabled = e.target.checked;
          // Also update legacy global for backward compatibility (front only)
          if (toggleSide === 'front') {
            self.textToggleEnabled = e.target.checked;
          }
          self.toggleFieldVisibility('text-customizer-section-' + toggleSide, e.target.checked);
          self.clearValidationError('text', toggleSide);
          log('v3.15.75: Text toggle changed for', toggleSide + ':', e.target.checked);
        });
      }

      // Logo toggle for this side
      var logoToggle = document.getElementById('toggle-logo-upload-' + side);
      if (logoToggle) {
        logoToggle.addEventListener('change', function(e) {
          var toggleSide = getSideFromElement(e.target);
          self.sideCustomizations[toggleSide].logoToggleEnabled = e.target.checked;
          // Also update legacy global for backward compatibility (front only)
          if (toggleSide === 'front') {
            self.logoToggleEnabled = e.target.checked;
          }
          self.toggleFieldVisibility('logo-customizer-section-' + toggleSide, e.target.checked);
          self.clearValidationError('logo', toggleSide);
          // If toggling off, also remove any uploaded logo for this side
          if (!e.target.checked) {
            self.removeLogo(toggleSide);
          }
          log('v3.15.75: Logo toggle changed for', toggleSide + ':', e.target.checked);
        });
      }
    });

    // Legacy: Also support old element IDs for backward compatibility
    var textToggle = document.getElementById('toggle-website-text');
    if (textToggle) {
      textToggle.addEventListener('change', function(e) {
        self.textToggleEnabled = e.target.checked;
        self.sideCustomizations.front.textToggleEnabled = e.target.checked;
        self.toggleFieldVisibility('text-customizer-section', e.target.checked);
        self.clearValidationError('text');
        log('v3.15.47: Text toggle changed (legacy):', self.textToggleEnabled);
      });
    }

    var logoToggle = document.getElementById('toggle-logo-upload');
    if (logoToggle) {
      logoToggle.addEventListener('change', function(e) {
        self.logoToggleEnabled = e.target.checked;
        self.sideCustomizations.front.logoToggleEnabled = e.target.checked;
        self.toggleFieldVisibility('logo-customizer-section', e.target.checked);
        self.clearValidationError('logo');
        if (!e.target.checked) {
          self.removeLogo();
        }
        log('v3.15.47: Logo toggle changed (legacy):', self.logoToggleEnabled);
      });
    }

    log('v3.15.75: Field toggle listeners attached for both sides');
  };

  /**
   * v3.15.47: Show/hide a field section with animation
   * @param {string} sectionId - ID of the section to toggle
   * @param {boolean} show - Whether to show or hide
   */
  DesignRadioSelector.prototype.toggleFieldVisibility = function(sectionId, show) {
    var section = document.getElementById(sectionId);
    if (!section) return;

    if (show) {
      section.classList.remove('is-hidden');
      // Allow CSS transition by setting max-height
      section.style.maxHeight = section.scrollHeight + 'px';
      setTimeout(function() {
        section.style.maxHeight = '';
      }, 300);
    } else {
      section.style.maxHeight = section.scrollHeight + 'px';
      // Force reflow
      section.offsetHeight;
      section.classList.add('is-hidden');
    }
  };

  /**
   * v3.15.47: Validate customizations based on toggle states
   * v3.15.78: Updated for per-side validation in double-sided mode
   * @returns {Object} { valid: boolean, errors: { front: {...}, back: {...} } }
   */
  DesignRadioSelector.prototype.validateCustomizations = function() {
    var self = this;
    var errors = { front: { text: null, logo: null }, back: { text: null, logo: null } };
    var valid = true;

    // Determine which sides to validate
    var sidesToValidate = ['front'];
    if (this.isDoubleSided && this.selectedDesigns.back) {
      sidesToValidate.push('back');
    }

    sidesToValidate.forEach(function(side) {
      var sideState = self.sideCustomizations[side];
      var suffix = '-' + side;

      // v3.15.78: Check per-side text toggle
      if (sideState.textToggleEnabled) {
        var textInput = document.getElementById('text-field-0' + suffix);
        if (textInput && !textInput.value.trim()) {
          errors[side].text = 'Please enter your website or hashtag';
          valid = false;
        }
      }

      // v3.15.78: Check per-side logo toggle
      if (sideState.logoToggleEnabled) {
        if (!sideState.logoUrl) {
          errors[side].logo = 'Please upload your logo';
          valid = false;
        }
      }
    });

    log('v3.15.78: Validation result:', { valid: valid, errors: errors, sidesToValidate: sidesToValidate });
    return { valid: valid, errors: errors };
  };

  /**
   * v3.15.47: Show validation errors on the UI
   * v3.15.78: Updated for per-side error structure
   * v4.2.1: Added auto-scroll and pulse animation for first error
   * @param {Object} errors - { front: { text, logo }, back: { text, logo } }
   */
  DesignRadioSelector.prototype.showValidationErrors = function(errors) {
    var sides = ['front', 'back'];
    var firstErrorElement = null;

    sides.forEach(function(side) {
      var sideErrors = errors[side];
      if (!sideErrors) return;

      var suffix = '-' + side;

      if (sideErrors.text) {
        var textInput = document.getElementById('text-field-0' + suffix);
        if (textInput) {
          textInput.classList.add('validation-error');
          // Track first error element for scrolling
          if (!firstErrorElement) {
            firstErrorElement = textInput;
          }
          // Add error message if not already present
          var field = textInput.closest('.text-customizer__field');
          if (field && !field.querySelector('.validation-message')) {
            var errorEl = document.createElement('div');
            errorEl.className = 'validation-message';
            errorEl.textContent = sideErrors.text;
            field.appendChild(errorEl);
          }
        }
      }

      if (sideErrors.logo) {
        var logoUploader = document.getElementById('logo-uploader' + suffix);
        if (logoUploader) {
          logoUploader.classList.add('validation-error');
          // Track first error element for scrolling
          if (!firstErrorElement) {
            firstErrorElement = logoUploader;
          }
          // Add error message if not already present
          var section = logoUploader.closest('.design-customizer__section');
          if (section && !section.querySelector('.validation-message')) {
            var errorEl = document.createElement('div');
            errorEl.className = 'validation-message';
            errorEl.textContent = sideErrors.logo;
            section.appendChild(errorEl);
          }
        }
      }
    });

    // v4.2.1: Auto-scroll to first error and apply pulse animation
    if (firstErrorElement) {
      this.scrollToValidationError(firstErrorElement);
    }
  };

  /**
   * v4.2.1: Scroll to validation error and apply pulse animation
   * @param {HTMLElement} element - The element with the validation error
   */
  DesignRadioSelector.prototype.scrollToValidationError = function(element) {
    if (!element) return;

    // Find the parent section to highlight
    var section = element.closest('.design-customizer__section');
    if (section) {
      section.classList.add('validation-highlight');
      // Remove highlight after animation completes
      setTimeout(function() {
        section.classList.remove('validation-highlight');
      }, 2500);
    }

    // Scroll the element into view with offset for header
    setTimeout(function() {
      var elementRect = element.getBoundingClientRect();
      var offset = 120; // Account for sticky header
      var scrollTop = window.pageYOffset + elementRect.top - offset;

      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });

      // Apply pulse animation after scroll completes
      setTimeout(function() {
        element.classList.add('validation-pulse');
        // Focus the element if it's an input
        if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
          element.focus();
        }
        // Remove pulse class after animation completes (3 cycles at 0.6s = 1.8s)
        setTimeout(function() {
          element.classList.remove('validation-pulse');
        }, 2000);
      }, 400); // Wait for scroll to mostly complete
    }, 50);

    log('v4.2.1: Scrolling to validation error:', element.id);
  };

  /**
   * v3.15.47: Clear all validation errors
   * v3.15.78: Updated for per-side validation
   */
  DesignRadioSelector.prototype.clearValidationErrors = function() {
    var sides = ['front', 'back'];
    var self = this;
    sides.forEach(function(side) {
      self.clearValidationError('text', side);
      self.clearValidationError('logo', side);
    });
  };

  /**
   * v3.15.47: Clear validation error for a specific field
   * @param {string} fieldType - 'text' or 'logo'
   */
  DesignRadioSelector.prototype.clearValidationError = function(fieldType, side) {
    // v3.15.75: Support per-side element IDs
    var suffix = side ? '-' + side : '';

    if (fieldType === 'text') {
      var textInput = document.getElementById('text-field-0' + suffix);
      if (textInput) {
        textInput.classList.remove('validation-error');
        var field = textInput.closest('.text-customizer__field');
        if (field) {
          var errorEl = field.querySelector('.validation-message');
          if (errorEl) errorEl.remove();
        }
      }
    } else if (fieldType === 'logo') {
      var logoUploader = document.getElementById('logo-uploader' + suffix);
      if (logoUploader) {
        logoUploader.classList.remove('validation-error');
        var section = logoUploader.closest('.design-customizer__section');
        if (section) {
          var errorEl = section.querySelector('.validation-message');
          if (errorEl) errorEl.remove();
        }
      }
    }
  };

  /**
   * v3.15.75: Remove uploaded logo (called when logo toggle is turned off)
   * @param {string} side - Optional: 'front' or 'back'. If not provided, uses legacy element IDs.
   */
  DesignRadioSelector.prototype.removeLogo = function(side) {
    // v3.15.75: Support per-side element IDs
    var suffix = side ? '-' + side : '';

    var logoInput = document.getElementById('logo-input' + suffix);
    if (logoInput) logoInput.value = '';

    var logoPreview = document.getElementById('logo-preview' + suffix);
    if (logoPreview) {
      logoPreview.style.display = 'none';
      logoPreview.src = '';
    }

    // v4.2.1: Also hide PDF icon
    var pdfIcon = document.getElementById('logo-pdf-icon' + suffix);
    if (pdfIcon) {
      pdfIcon.style.display = 'none';
    }

    var logoUploader = document.getElementById('logo-uploader' + suffix);
    if (logoUploader) {
      logoUploader.classList.remove('has-logo');
      logoUploader.classList.remove('has-pdf'); // v4.2.1
    }

    // For text and remove button, need to scope to the right panel
    var panel = side ? document.getElementById('design-customizer-' + side) : document;
    if (panel) {
      var logoText = panel.querySelector('.logo-uploader__text');
      if (logoText) logoText.style.display = '';

      var logoRemove = panel.querySelector('.logo-uploader__remove');
      if (logoRemove) logoRemove.style.display = 'none';
    }

    var logoUrlInput = document.getElementById('logo-url' + suffix);
    if (logoUrlInput) logoUrlInput.value = '';

    // v3.15.75: Update per-side state
    if (side) {
      this.sideCustomizations[side].logoUrl = null;
      this.sideCustomizations[side].logoFileName = null; // v4.2.1
    }
    // Legacy global
    if (!side || side === 'front') {
      this.uploadedLogoUrl = null;
    }

    log('v4.2.1: Logo removed for side:', side || 'legacy');
  };

  /**
   * Set up color swatch click listeners
   * v3.15.55: Added CMYK support and custom color picker integration
   * v3.15.75: Updated for per-side customization
   */
  DesignRadioSelector.prototype.setupColorSwatchListeners = function() {
    var self = this;
    var swatches = document.querySelectorAll('.color-swatch:not(.color-swatch--custom)');

    swatches.forEach(function(swatch) {
      swatch.addEventListener('click', function(e) {
        e.preventDefault();

        // v3.15.75: Determine which side this swatch belongs to
        var side = getSideFromElement(this);
        var panel = this.closest('.design-customizer') || document;

        // Check FIRST if this swatch was already selected (before clearing)
        var wasSelected = this.classList.contains('is-selected');

        // Remove selection from all swatches in THIS panel only
        var panelSwatches = panel.querySelectorAll('.color-swatch');
        panelSwatches.forEach(function(s) {
          s.classList.remove('is-selected');
        });
        if (!wasSelected) {
          this.classList.add('is-selected');
          var color = this.dataset.color;
          var cmyk = {
            c: parseInt(this.dataset.cmykC, 10) || 0,
            m: parseInt(this.dataset.cmykM, 10) || 0,
            y: parseInt(this.dataset.cmykY, 10) || 0,
            k: parseInt(this.dataset.cmykK, 10) || 0
          };

          // v3.15.75: Store in per-side state
          self.sideCustomizations[side].backgroundColor = color;
          self.sideCustomizations[side].backgroundCMYK = cmyk;

          // Legacy globals (for backward compatibility)
          if (side === 'front') {
            self.selectedBackgroundColor = color;
            self.selectedBackgroundCMYK = cmyk;
          }

          log('v3.15.75: Background color selected for', side + ':', color, 'CMYK:', cmyk);
        } else {
          // v3.15.75: Clear per-side state
          self.sideCustomizations[side].backgroundColor = null;
          self.sideCustomizations[side].backgroundCMYK = null;

          // Legacy globals
          if (side === 'front') {
            self.selectedBackgroundColor = null;
            self.selectedBackgroundCMYK = null;
          }

          log('v3.15.75: Background color deselected for', side);
        }
      });
    });

    // v3.15.67: Initialize Alwan color picker (replaces iro.js modal)
    this.initColorPicker();

    log('v3.15.75: Color swatch listeners attached for both sides');
  };

  /**
   * v3.15.55: Convert hex color to CMYK (approximate conversion)
   * Note: For print accuracy, preset swatches should use manually curated CMYK values
   */
  DesignRadioSelector.prototype.hexToCMYK = function(hex) {
    hex = hex.replace('#', '');
    var r = parseInt(hex.substring(0, 2), 16) / 255;
    var g = parseInt(hex.substring(2, 4), 16) / 255;
    var b = parseInt(hex.substring(4, 6), 16) / 255;

    var k = 1 - Math.max(r, g, b);
    if (k === 1) {
      return { c: 0, m: 0, y: 0, k: 100 };
    }

    return {
      c: Math.round(((1 - r - k) / (1 - k)) * 100),
      m: Math.round(((1 - g - k) / (1 - k)) * 100),
      y: Math.round(((1 - b - k) / (1 - k)) * 100),
      k: Math.round(k * 100)
    };
  };

  /**
   * v3.15.55: Convert CMYK to hex color
   */
  DesignRadioSelector.prototype.cmykToHex = function(cmyk) {
    var c = cmyk.c / 100, m = cmyk.m / 100, y = cmyk.y / 100, k = cmyk.k / 100;
    var r = Math.round(255 * (1 - c) * (1 - k));
    var g = Math.round(255 * (1 - m) * (1 - k));
    var b = Math.round(255 * (1 - y) * (1 - k));
    return '#' + [r, g, b].map(function(n) {
      return n.toString(16).padStart(2, '0');
    }).join('').toUpperCase();
  };

  /**
   * v3.15.68: Initialize custom branded color picker with vanilla-colorful
   * Custom popover with HEX/CMYK inputs
   * v3.15.77: Updated for per-side color pickers (front/back)
   */
  DesignRadioSelector.prototype.initColorPicker = function() {
    var self = this;

    // v3.15.77: Check for per-side elements (front/back)
    var frontBtn = document.getElementById('custom-color-btn-front');
    var backBtn = document.getElementById('custom-color-btn-back');
    var frontPopover = document.getElementById('color-picker-popover-front');
    var backPopover = document.getElementById('color-picker-popover-back');

    if (!frontBtn && !backBtn) {
      log('Custom color picker elements not found, skipping init');
      return;
    }

    // Track which side's picker is currently open
    this._currentPickerSide = null;

    // Wait for Web Component to be defined
    customElements.whenDefined('hex-color-picker').then(function() {
      log('vanilla-colorful loaded, initializing pickers for both sides');
      if (frontBtn && frontPopover) {
        self._setupPickerListenersForSide('front');
      }
      if (backBtn && backPopover) {
        self._setupPickerListenersForSide('back');
      }
    }).catch(function() {
      // Fallback: try again after a delay
      setTimeout(function() {
        if (frontBtn && frontPopover) {
          self._setupPickerListenersForSide('front');
        }
        if (backBtn && backPopover) {
          self._setupPickerListenersForSide('back');
        }
      }, 500);
    });
  };

  /**
   * v3.15.68: Setup picker event listeners
   * v3.15.77: Updated to accept side parameter (front/back)
   */
  DesignRadioSelector.prototype._setupPickerListenersForSide = function(side) {
    var self = this;
    var suffix = '-' + side;
    var customBtn = document.getElementById('custom-color-btn' + suffix);
    var popover = document.getElementById('color-picker-popover' + suffix);
    var picker = document.getElementById('color-picker-element' + suffix);
    var hexInput = document.getElementById('color-picker-hex' + suffix);
    var cInput = document.getElementById('color-picker-c' + suffix);
    var mInput = document.getElementById('color-picker-m' + suffix);
    var yInput = document.getElementById('color-picker-y' + suffix);
    var kInput = document.getElementById('color-picker-k' + suffix);

    if (!customBtn || !popover) {
      log('Color picker elements not found for side:', side);
      return;
    }

    this._pickerOpen = false;
    this._updatingFromPicker = false;
    this._updatingFromInput = false;

    // Toggle popover on button click
    customBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      self._currentPickerSide = side;
      self._togglePopover(side);
    });

    // v3.15.70: Close on click outside (using mousedown to avoid closing during picker drag)
    document.addEventListener('mousedown', function(e) {
      if (self._pickerOpen && self._currentPickerSide === side) {
        var activePopover = document.getElementById('color-picker-popover-' + side);
        var activeBtn = document.getElementById('custom-color-btn-' + side);
        if (activePopover && !activePopover.contains(e.target) && e.target !== activeBtn && !activeBtn.contains(e.target)) {
          self._closePopover(true); // Apply on close
        }
      }
    });

    // v3.15.70: Apply button click
    var applyBtn = document.getElementById('color-picker-apply' + suffix);
    if (applyBtn) {
      applyBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        self._currentPickerSide = side;
        self._closePopover(true); // Apply color
      });
    }

    // Close on Escape key (only once, not per side)
    if (side === 'front') {
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self._pickerOpen) {
          self._closePopover(false); // Don't apply on escape
        }
      });

      // v3.15.69: Close on scroll (prevents floating disconnected popover)
      window.addEventListener('scroll', function() {
        if (self._pickerOpen) {
          self._closePopover(true); // Apply on scroll close
        }
      }, true); // Use capture to catch scroll on any element
    }

    // v3.15.70: Preview swatch element
    var previewSwatch = document.getElementById('color-picker-preview' + suffix);

    // Picker color change
    if (picker) {
      picker.addEventListener('color-changed', function(e) {
        if (self._updatingFromInput) return;
        self._updatingFromPicker = true;

        var hex = e.detail.value.toUpperCase();
        var cmyk = self.hexToCMYK(hex);

        // Update inputs
        if (hexInput) hexInput.value = hex;
        self._updateCMYKInputs(cmyk, side);

        // v3.15.70: Update preview swatch (not the button until applied)
        if (previewSwatch) previewSwatch.style.backgroundColor = hex;

        self._updatingFromPicker = false;
      });
    }

    // HEX input change
    if (hexInput) {
      hexInput.addEventListener('input', function() {
        if (self._updatingFromPicker) return;

        var hex = hexInput.value.trim();
        if (hex.charAt(0) !== '#') hex = '#' + hex;

        if (/^#[0-9A-Fa-f]{6}$/i.test(hex)) {
          self._updatingFromInput = true;
          hex = hex.toUpperCase();
          if (picker) picker.color = hex;
          var cmyk = self.hexToCMYK(hex);
          self._updateCMYKInputs(cmyk, side);
          // v3.15.70: Update preview swatch
          if (previewSwatch) previewSwatch.style.backgroundColor = hex;
          self._updatingFromInput = false;
        }
      });

      hexInput.addEventListener('blur', function() {
        var hex = hexInput.value.trim().toUpperCase();
        if (hex.charAt(0) !== '#') hex = '#' + hex;
        if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
          hexInput.value = hex;
        }
      });
    }

    // CMYK input handlers
    var cmykHandler = function() {
      if (self._updatingFromPicker) return;

      var c = parseInt(cInput.value, 10) || 0;
      var m = parseInt(mInput.value, 10) || 0;
      var y = parseInt(yInput.value, 10) || 0;
      var k = parseInt(kInput.value, 10) || 0;

      c = Math.max(0, Math.min(100, c));
      m = Math.max(0, Math.min(100, m));
      y = Math.max(0, Math.min(100, y));
      k = Math.max(0, Math.min(100, k));

      var hex = self.cmykToHex({ c: c, m: m, y: y, k: k });

      self._updatingFromInput = true;
      if (picker) picker.color = hex;
      if (hexInput) hexInput.value = hex;
      // v3.15.70: Update preview swatch
      if (previewSwatch) previewSwatch.style.backgroundColor = hex;
      self._updatingFromInput = false;
    };

    if (cInput) cInput.addEventListener('input', cmykHandler);
    if (mInput) mInput.addEventListener('input', cmykHandler);
    if (yInput) yInput.addEventListener('input', cmykHandler);
    if (kInput) kInput.addEventListener('input', cmykHandler);

    // Clamp values on blur
    [cInput, mInput, yInput, kInput].forEach(function(input) {
      if (input) {
        input.addEventListener('blur', function() {
          var val = parseInt(input.value, 10);
          if (isNaN(val)) val = 0;
          input.value = Math.max(0, Math.min(100, val));
        });
      }
    });

    log('Custom color picker initialized for side:', side);
  };

  /**
   * v3.15.70: Toggle popover visibility
   * v3.15.77: Updated to use current picker side
   */
  DesignRadioSelector.prototype._togglePopover = function(side) {
    if (this._pickerOpen && this._currentPickerSide === side) {
      this._closePopover(false); // Toggle close = don't apply
    } else {
      // Close any other open picker first
      if (this._pickerOpen) {
        this._closePopover(false);
      }
      this._currentPickerSide = side;
      this._openPopover();
    }
  };

  /**
   * v3.15.70: Open popover with fixed positioning - defaults to BELOW button
   * v3.15.77: Updated to use _currentPickerSide for element IDs
   */
  DesignRadioSelector.prototype._openPopover = function() {
    var side = this._currentPickerSide || 'front';
    var suffix = '-' + side;
    var popover = document.getElementById('color-picker-popover' + suffix);
    var customBtn = document.getElementById('custom-color-btn' + suffix);
    var picker = document.getElementById('color-picker-element' + suffix);
    var hexInput = document.getElementById('color-picker-hex' + suffix);
    var previewSwatch = document.getElementById('color-picker-preview' + suffix);

    if (!popover || !customBtn) return;

    // v3.15.77: Get initial color from the correct side's state
    var sideState = this.sideCustomizations && this.sideCustomizations[side];
    var initialColor = (sideState && sideState.backgroundColor) || this.selectedBackgroundColor || '#FFFFFF';
    if (picker) picker.color = initialColor;
    if (hexInput) hexInput.value = initialColor.toUpperCase();
    this._updateCMYKInputs(this.hexToCMYK(initialColor), side);
    // v3.15.70: Initialize preview swatch
    if (previewSwatch) previewSwatch.style.backgroundColor = initialColor;

    // v3.15.70: Calculate position based on button's viewport location
    var btnRect = customBtn.getBoundingClientRect();
    var popoverWidth = 280; // Approximate width (slightly increased)
    var popoverHeight = 400; // Approximate height (includes apply button)
    var gap = 12; // Gap between button and popover

    // Center horizontally on the button
    var left = btnRect.left + (btnRect.width / 2) - (popoverWidth / 2);

    // v3.15.70: Default to BELOW the button
    var top = btnRect.bottom + gap;
    var arrowOnTop = true; // Arrow points UP by default

    // Keep within viewport bounds
    var viewportWidth = window.innerWidth;
    var viewportHeight = window.innerHeight;

    // Horizontal bounds check
    if (left < 10) left = 10;
    if (left + popoverWidth > viewportWidth - 10) {
      left = viewportWidth - popoverWidth - 10;
    }

    // v3.15.70: If not enough space below, position above
    if (top + popoverHeight > viewportHeight - 10) {
      top = btnRect.top - popoverHeight - gap;
      arrowOnTop = false; // Arrow points DOWN
    }

    // Set arrow direction class
    if (arrowOnTop) {
      popover.classList.add('arrow-top');
    } else {
      popover.classList.remove('arrow-top');
    }

    // Calculate arrow position to point at button center
    var buttonCenterX = btnRect.left + (btnRect.width / 2);
    var arrowLeft = buttonCenterX - left;
    popover.style.setProperty('--arrow-left', arrowLeft + 'px');

    popover.style.left = left + 'px';
    popover.style.top = top + 'px';

    popover.classList.add('is-open');
    if (customBtn) customBtn.classList.add('picker-open');
    this._pickerOpen = true;

    log('Color picker opened at position:', { left: left, top: top });
  };

  /**
   * v3.15.70: Close popover with optional color application
   * v3.15.77: Updated to use _currentPickerSide for element IDs
   * @param {boolean} applyColor - Whether to apply the selected color
   */
  DesignRadioSelector.prototype._closePopover = function(applyColor) {
    var side = this._currentPickerSide || 'front';
    var suffix = '-' + side;
    var popover = document.getElementById('color-picker-popover' + suffix);
    var customBtn = document.getElementById('custom-color-btn' + suffix);
    var hexInput = document.getElementById('color-picker-hex' + suffix);

    if (!popover) return;

    // v3.15.70: Only apply color if explicitly requested
    if (applyColor) {
      var hex = hexInput ? hexInput.value.toUpperCase() : '#FFFFFF';
      if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
        var cmyk = this.hexToCMYK(hex);
        this.applyCustomColor(hex, cmyk, side);

        // v3.15.70: Update swatch button to show applied color
        if (customBtn) {
          customBtn.classList.add('has-color');
          customBtn.style.setProperty('--applied-color', hex);
          customBtn.style.backgroundColor = hex;
        }
      }
    }

    popover.classList.remove('is-open');
    popover.classList.remove('arrow-top');
    if (customBtn) customBtn.classList.remove('picker-open');
    this._pickerOpen = false;
    this._currentPickerSide = null;

    log('Color picker closed for side:', side, applyColor ? '(color applied)' : '(cancelled)');
  };

  /**
   * v3.15.68: Update CMYK input values
   * v3.15.77: Updated to accept side parameter
   */
  DesignRadioSelector.prototype._updateCMYKInputs = function(cmyk, side) {
    var suffix = side ? ('-' + side) : ('-' + (this._currentPickerSide || 'front'));
    var cInput = document.getElementById('color-picker-c' + suffix);
    var mInput = document.getElementById('color-picker-m' + suffix);
    var yInput = document.getElementById('color-picker-y' + suffix);
    var kInput = document.getElementById('color-picker-k' + suffix);

    if (cInput) cInput.value = cmyk.c;
    if (mInput) mInput.value = cmyk.m;
    if (yInput) yInput.value = cmyk.y;
    if (kInput) kInput.value = cmyk.k;
  };

  /**
   * v3.15.68: Apply custom color
   * v3.15.77: Updated to accept side parameter for per-side customization
   */
  DesignRadioSelector.prototype.applyCustomColor = function(hexColor, cmyk, side) {
    side = side || this._currentPickerSide || 'front';
    var suffix = '-' + side;

    // v3.15.77: Update per-side state
    if (this.sideCustomizations && this.sideCustomizations[side]) {
      this.sideCustomizations[side].backgroundColor = hexColor.toUpperCase();
      this.sideCustomizations[side].backgroundCMYK = cmyk;
    }

    // Also update global state for backwards compatibility
    this.selectedBackgroundColor = hexColor.toUpperCase();
    this.selectedBackgroundCMYK = cmyk;

    // Deselect preset swatches in this side's panel only
    var panelSelector = '#color-customizer-section-' + side;
    var panel = document.querySelector(panelSelector);
    if (panel) {
      var presetSwatches = panel.querySelectorAll('.color-swatch:not(.color-swatch--custom)');
      presetSwatches.forEach(function(s) {
        s.classList.remove('is-selected');
      });
    }

    // Mark custom as selected
    var customBtn = document.getElementById('custom-color-btn' + suffix);
    if (customBtn) {
      customBtn.classList.add('is-selected');
      customBtn.style.backgroundColor = hexColor;
      // Hide plus icon when color is selected
      var plusIcon = customBtn.querySelector('.color-swatch__icon');
      if (plusIcon) plusIcon.style.display = 'none';
    }

    log('v3.15.77: Custom color applied for side:', side, hexColor, 'CMYK:', cmyk);
  };

  /**
   * Set up text input listeners with character counting
   * v3.15.75: Updated for per-side customization
   */
  DesignRadioSelector.prototype.setupTextInputListeners = function() {
    var self = this;
    var textInputs = document.querySelectorAll('.text-customizer__input');

    textInputs.forEach(function(input) {
      // Update character count on input
      input.addEventListener('input', function() {
        var field = this.closest('.text-customizer__field');
        if (field) {
          var currentCount = field.querySelector('.text-customizer__char-current');
          if (currentCount) {
            currentCount.textContent = this.value.length;
          }
        }

        // v3.15.75: Store text value in per-side state
        var side = getSideFromElement(this);
        self.sideCustomizations[side].textValue = this.value;
      });

      // Also update on focus (in case value was pre-filled)
      input.addEventListener('focus', function() {
        var field = this.closest('.text-customizer__field');
        if (field) {
          var currentCount = field.querySelector('.text-customizer__char-current');
          if (currentCount) {
            currentCount.textContent = this.value.length;
          }
        }
      });
    });

    log('v3.15.75: Text input listeners attached for both sides');
  };

  /**
   * Set up logo upload listeners (click, drag/drop)
   * v3.15.75: Updated for per-side customization
   */
  DesignRadioSelector.prototype.setupLogoUploadListeners = function() {
    var self = this;
    var sides = ['front', 'back'];

    sides.forEach(function(side) {
      var uploader = document.getElementById('logo-uploader-' + side);
      var fileInput = document.getElementById('logo-input-' + side);

      if (!uploader || !fileInput) {
        // Try legacy IDs for backward compatibility
        if (side === 'front') {
          uploader = document.getElementById('logo-uploader');
          fileInput = document.getElementById('logo-input');
        }
        if (!uploader || !fileInput) {
          return; // Skip this side if elements not found
        }
      }

      // v4.3.23: On mobile, restrict file input to PNG/JPG only (no SVG)
      // This makes the file picker show only supported formats by default
      if (window.innerWidth < 768) {
        fileInput.setAttribute('accept', 'image/png,image/jpeg');
        log('v4.3.23: Mobile detected - restricted file input to PNG/JPG only');
      }

      // Click to upload
      uploader.addEventListener('click', function(e) {
        var removeBtn = this.querySelector('.logo-uploader__remove');
        if (e.target !== removeBtn && !e.target.closest('.logo-uploader__remove')) {
          fileInput.click();
        }
      });

      // File selected
      fileInput.addEventListener('change', function(e) {
        if (this.files && this.files[0]) {
          var fileSide = getSideFromElement(this);
          self.handleLogoFile(this.files[0], fileSide);
        }
      });

      // Drag and drop
      uploader.addEventListener('dragover', function(e) {
        e.preventDefault();
        e.stopPropagation();
        this.classList.add('is-dragover');
      });

      uploader.addEventListener('dragleave', function(e) {
        e.preventDefault();
        e.stopPropagation();
        this.classList.remove('is-dragover');
      });

      uploader.addEventListener('drop', function(e) {
        e.preventDefault();
        e.stopPropagation();
        this.classList.remove('is-dragover');

        var files = e.dataTransfer.files;
        if (files && files[0]) {
          var dropSide = getSideFromElement(this);
          self.handleLogoFile(files[0], dropSide);
        }
      });

      // Remove button
      var removeBtn = uploader.querySelector('.logo-uploader__remove');
      if (removeBtn) {
        removeBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          var removeSide = getSideFromElement(this);
          self.removeLogo(removeSide);
        });
      }
    });

    log('v3.15.75: Logo upload listeners attached for both sides');
  };

  /**
   * Handle logo file selection
   * v3.15.75: Updated for per-side customization
   * v4.2.1: Added PDF support and S3 upload
   * @param {File} file - The selected file
   * @param {string} side - 'front' or 'back'
   */
  DesignRadioSelector.prototype.handleLogoFile = function(file, side) {
    var self = this;
    side = side || 'front';
    var suffix = '-' + side;

    // v4.2.4: Get error element for inline validation messages
    var errorEl = document.getElementById('logo-error' + suffix);

    // Helper to show error message
    function showError(title, message) {
      if (errorEl) {
        errorEl.innerHTML = '<strong>' + title + '</strong>' + message;
        errorEl.style.display = 'block';
      }
    }

    // Helper to clear error message
    function clearError() {
      if (errorEl) {
        errorEl.style.display = 'none';
      }
    }

    // v4.2.4: PDF files not supported yet (fabric.js cannot render PDFs directly)
    // TODO: Add server-side PDF-to-PNG conversion using pdf-poppler or sharp on Lambda
    if (file.type === 'application/pdf') {
      log('v4.2.4: PDF rejected - not yet supported');
      showError('PDF files are not supported yet', 'Please export your logo as PNG or JPG and upload again.');
      return;
    }

    // v4.3.23: SVG files not supported on mobile (complex SVGs crash PitchPrint iframe)
    var isMobileDevice = window.innerWidth < 768;
    if (file.type === 'image/svg+xml' && isMobileDevice) {
      log('v4.3.23: SVG rejected on mobile - causes iframe crashes');
      showError('SVG files are not supported on mobile', 'Please upload a PNG or JPG file instead.');
      // v4.3.23: Clear file input to release memory
      var fileInput = document.getElementById('logo-input-' + side) || document.getElementById('logo-input' + suffix);
      if (fileInput) fileInput.value = '';
      // v4.3.23: Clear design cache to prevent using potentially corrupted cached data
      if (window.DesignTemplateCache && typeof window.DesignTemplateCache.clear === 'function') {
        window.DesignTemplateCache.clear();
        log('v4.3.23: Design template cache cleared after SVG rejection');
      }
      return;
    }

    var validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      log('Invalid file type:', file.type);
      var formatMsg = isMobileDevice ? 'Please upload a PNG or JPG file.' : 'Please upload a PNG, JPG, or SVG file.';
      showError('Invalid file type', formatMsg);
      return;
    }

    // Validate file size (max 10MB for logos)
    var maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      log('File too large:', file.size);
      showError('File too large', 'Maximum file size is 10MB.');
      return;
    }

    // Clear any previous error on successful validation
    clearError();

    log('v4.2.1: Processing logo file for', side + ':', file.name, file.type, file.size);

    // v3.15.75: Get elements for the correct side
    var preview = document.getElementById('logo-preview' + suffix) || document.getElementById('logo-preview');
    var pdfIcon = document.getElementById('logo-pdf-icon' + suffix);
    var uploader = document.getElementById('logo-uploader' + suffix) || document.getElementById('logo-uploader');
    var panel = uploader ? uploader.closest('.design-customizer') : document;
    var uploadText = panel ? panel.querySelector('.logo-uploader__text') : null;
    var removeBtn = panel ? panel.querySelector('.logo-uploader__remove') : null;

    var isPdf = file.type === 'application/pdf';

    // v4.2.1: Handle preview based on file type
    if (isPdf) {
      // Show PDF icon instead of image preview
      if (pdfIcon) pdfIcon.style.display = 'flex';
      if (preview) preview.style.display = 'none';
      if (uploader) {
        uploader.classList.add('has-pdf');
        uploader.classList.remove('has-logo');
      }
    } else {
      // Show image preview for PNG, JPG, SVG
      var reader = new FileReader();
      reader.onload = function(e) {
        if (preview) {
          preview.src = e.target.result;
          preview.style.display = 'block';
        }
        if (pdfIcon) pdfIcon.style.display = 'none';
      };
      reader.readAsDataURL(file);
      if (uploader) {
        uploader.classList.add('has-logo');
        uploader.classList.remove('has-pdf');
      }
    }

    if (uploadText) uploadText.style.display = 'none';
    if (removeBtn) removeBtn.style.display = 'block';

    // Upload to S3 and get CDN URL
    this.uploadLogoToServer(file, side);
  };

  /**
   * Upload logo to S3 via presigned URL
   * v4.2.1: Refactored from base64 to S3 upload
   * @param {File} file - The file to upload
   * @param {string} side - 'front' or 'back'
   */
  DesignRadioSelector.prototype.uploadLogoToServer = function(file, side) {
    var self = this;
    side = side || 'front';
    var suffix = '-' + side;

    var uploader = document.getElementById('logo-uploader' + suffix) || document.getElementById('logo-uploader');

    // Show uploading state
    if (uploader) uploader.classList.add('is-uploading');

    // v4.2.1: Upload to S3 via presigned URL
    this.getLogoPresignedUrl(file, side)
      .then(function(presignResponse) {
        log('v4.2.1: Got presigned URL for logo:', presignResponse.uploadId);
        return self.uploadLogoToS3(presignResponse.uploadUrl, file)
          .then(function() {
            return presignResponse;
          });
      })
      .then(function(presignResponse) {
        // Build CDN URL (API returns originalKey, not key)
        var cdnUrl = 'https://img.popsigns.co/' + presignResponse.originalKey;

        // Store in per-side state
        self.sideCustomizations[side].logoUrl = cdnUrl;
        self.sideCustomizations[side].logoFileName = file.name;

        // Legacy global (for backward compatibility)
        if (side === 'front') {
          self.uploadedLogoUrl = cdnUrl;
        }

        log('v4.2.1: Logo uploaded for', side, ':', cdnUrl);

        if (uploader) uploader.classList.remove('is-uploading');

        // Store in hidden input
        var logoUrlInput = document.getElementById('logo-url' + suffix) || document.getElementById('logo-url');
        if (logoUrlInput) {
          logoUrlInput.value = cdnUrl;
        }
      })
      .catch(function(error) {
        log('v4.2.1: Logo upload failed:', error);
        if (uploader) uploader.classList.remove('is-uploading');
        alert('Upload failed. Please try again.');
        self.removeLogo(side);
      });
  };

  /**
   * Get presigned URL for logo upload
   * v4.2.1: New method for S3 upload
   * @param {File} file - The file to upload
   * @param {string} side - 'front' or 'back'
   * @returns {Promise} Resolves with presign response
   */
  DesignRadioSelector.prototype.getLogoPresignedUrl = function(file, side) {
    var apiEndpoint = window.POPSIGNS_API_ENDPOINT || 'https://fz8y24ewil.execute-api.us-east-1.amazonaws.com/staging';
    var customerId = this.getLogoCustomerId();
    var shape = this.currentShape || 'classic';

    return fetch(apiEndpoint + '/api/uploads/presign', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shop-Domain': window.Shopify && window.Shopify.shop ? window.Shopify.shop : 'popsigns.co'
      },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type,
        customerId: customerId,
        shape: shape,
        side: side
      })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Failed to get presigned URL: ' + response.statusText);
      }
      return response.json();
    });
  };

  /**
   * Upload file to S3 using presigned URL
   * v4.2.1: New method for S3 upload
   * @param {string} presignedUrl - The presigned S3 URL
   * @param {File} file - The file to upload
   * @returns {Promise} Resolves when upload completes
   */
  DesignRadioSelector.prototype.uploadLogoToS3 = function(presignedUrl, file) {
    return new Promise(function(resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error('S3 upload failed: ' + xhr.statusText));
        }
      };

      xhr.onerror = function() {
        reject(new Error('Network error during S3 upload'));
      };

      xhr.send(file);
    });
  };

  /**
   * Get customer ID for upload tracking
   * v4.2.1: New method for S3 upload
   * @returns {string} Customer ID or temporary ID
   */
  DesignRadioSelector.prototype.getLogoCustomerId = function() {
    // Get customer ID from Shopify or generate temporary ID
    var customerId = window.ShopifyAnalytics &&
                     window.ShopifyAnalytics.meta &&
                     window.ShopifyAnalytics.meta.page &&
                     window.ShopifyAnalytics.meta.page.customerId;
    return customerId ? String(customerId) : 'temp-' + Date.now();
  };

  // v3.15.75: removeLogo function moved to line ~917 with per-side support

  /**
   * v3.15.75: Gather customizations for a specific side
   * @param {string} side - 'front' or 'back'
   * @returns {Object} Customization object for the side
   */
  DesignRadioSelector.prototype.gatherCustomizationsForSide = function(side) {
    var sideState = this.sideCustomizations[side];
    var customizations = {};
    var suffix = '-' + side;

    // Background color from per-side state
    if (sideState.backgroundColor) {
      customizations.backgroundColor = sideState.backgroundColor;
      if (sideState.backgroundCMYK) {
        customizations.backgroundCMYK = sideState.backgroundCMYK;
      }
    }

    // v4.6.0: Logo-only layout handling
    var layoutType = this.config.designLayout || 'centered';
    var isLogoOnlyLayout = (layoutType === 'logo_only_left' || layoutType === 'logo_only_center');

    // Deletion flags based on per-side toggle states
    if (isLogoOnlyLayout) {
      // Logo-only layouts: always delete text and divider (they don't exist in template)
      customizations.deleteText = true;
      customizations.deleteDivider = true;
      // Delete logo placeholder only if user hasn't uploaded a logo
      if (!sideState.logoToggleEnabled) {
        customizations.deleteLogo = true;
      }
      log('v4.6.0: Logo-only layout for', side, '- deleting text/divider, logoToggle:', sideState.logoToggleEnabled);
    } else {
      // Standard layouts: deletion based on toggle states
      if (!sideState.textToggleEnabled) {
        customizations.deleteText = true;
        log('v3.15.75: Text toggle OFF for', side, '- will delete text');
      }
      if (!sideState.logoToggleEnabled) {
        customizations.deleteLogo = true;
        log('v3.15.75: Logo toggle OFF for', side, '- will delete logo');
      }
      if (!sideState.textToggleEnabled || !sideState.logoToggleEnabled) {
        customizations.deleteDivider = true;
      }
    }

    // Text edits from per-side input
    if (sideState.textToggleEnabled) {
      var textInput = document.getElementById('text-field-0' + suffix);
      if (textInput) {
        var value = textInput.value.trim();
        if (value) {
          var targetIndex = parseInt(textInput.getAttribute('data-target-index'), 10);
          if (!isNaN(targetIndex)) {
            customizations.textEdits = [{
              targetIndex: targetIndex,
              text: value
            }];
            log('v3.15.75: Text edit for', side, '- targetIndex:', targetIndex, 'text:', value);
          }
        }
      }
    }

    // Logo from per-side state
    // v4.2.8: Add cache-buster to force fresh request with CORS headers
    // (Prevents browser from serving cached non-CORS version to PitchPrint iframe)
    if (sideState.logoToggleEnabled && sideState.logoUrl) {
      var logoUrl = sideState.logoUrl;
      var separator = logoUrl.indexOf('?') === -1 ? '?' : '&';
      customizations.logoUrl = logoUrl + separator + '_cors=' + Date.now();
    }

    return customizations;
  };

  /**
   * Gather all customizations from the UI
   * v3.15.75: Returns per-side format for double-sided, single object for single-sided
   * @returns {Object} Customization object
   */
  DesignRadioSelector.prototype.gatherCustomizations = function() {
    // v3.15.75: For double-sided mode, gather per-side customizations
    if (this.isDoubleSidedMode) {
      var frontCustomizations = this.gatherCustomizationsForSide('front');
      var backCustomizations = this.gatherCustomizationsForSide('back');

      var result = {
        front: frontCustomizations,
        back: backCustomizations,
        perSideFormat: true,
        // v4.5.1: Layout configuration (centered or left-aligned)
        layoutConfig: { type: this.config.designLayout || 'centered' }
      };

      log('v4.5.1: Gathered per-side customizations (layout:', this.config.designLayout || 'centered', '):', result);
      return result;
    }

    // Single-sided mode: use front panel (backward compatible format)
    var customizations = this.gatherCustomizationsForSide('front');

    // Also fall back to legacy global states for backward compatibility
    if (!customizations.backgroundColor && this.selectedBackgroundColor) {
      customizations.backgroundColor = this.selectedBackgroundColor;
      if (this.selectedBackgroundCMYK) {
        customizations.backgroundCMYK = this.selectedBackgroundCMYK;
      }
    }

    // v4.2.8: Add cache-buster for legacy fallback too
    if (!customizations.logoUrl && this.uploadedLogoUrl) {
      var logoUrl = this.uploadedLogoUrl;
      var separator = logoUrl.indexOf('?') === -1 ? '?' : '&';
      customizations.logoUrl = logoUrl + separator + '_cors=' + Date.now();
    }

    // v4.5.1: Layout configuration (centered or left-aligned)
    customizations.layoutConfig = { type: this.config.designLayout || 'centered' };

    log('v4.5.1: Gathered customizations (single-sided, layout:', this.config.designLayout || 'centered', '):', customizations);
    return customizations;
  };

  /**
   * Check if a single side has customizations
   * @param {Object} sideCustomizations - Customization object for one side
   * @returns {boolean}
   */
  DesignRadioSelector.prototype.hasSideCustomizations = function(sideCustomizations) {
    if (!sideCustomizations) return false;
    return !!(
      sideCustomizations.backgroundColor ||
      (sideCustomizations.textEdits && sideCustomizations.textEdits.length > 0) ||
      sideCustomizations.logoUrl ||
      sideCustomizations.deleteText ||
      sideCustomizations.deleteLogo ||
      sideCustomizations.deleteDivider
    );
  };

  /**
   * Check if user made any customizations (including deletions)
   * v3.15.75: Handles both per-side format and legacy format
   * @param {Object} customizations - Customization object from gatherCustomizations
   * @returns {boolean}
   */
  DesignRadioSelector.prototype.hasCustomizations = function(customizations) {
    if (!customizations) return false;

    // v3.15.75: Per-side format
    if (customizations.perSideFormat) {
      return this.hasSideCustomizations(customizations.front) ||
             this.hasSideCustomizations(customizations.back);
    }

    // Legacy format
    return !!(
      customizations.backgroundColor ||
      (customizations.textEdits && customizations.textEdits.length > 0) ||
      customizations.logoUrl ||
      customizations.deleteText ||     // v3.15.47
      customizations.deleteLogo ||     // v3.15.47
      customizations.deleteDivider     // v3.15.47
    );
  };

  /**
   * v3.15.75: Copy all customizations from front panel to back panel
   * Deep copies state and updates all UI elements
   */
  DesignRadioSelector.prototype.copyFromFront = function() {
    log('v3.15.75: Copying customizations from front to back');

    var front = this.sideCustomizations.front;
    var back = this.sideCustomizations.back;

    // Deep copy state values
    back.textToggleEnabled = front.textToggleEnabled;
    back.logoToggleEnabled = front.logoToggleEnabled;
    back.backgroundColor = front.backgroundColor;
    back.backgroundCMYK = front.backgroundCMYK ? {
      c: front.backgroundCMYK.c,
      m: front.backgroundCMYK.m,
      y: front.backgroundCMYK.y,
      k: front.backgroundCMYK.k
    } : null;
    back.logoUrl = front.logoUrl;
    back.textValue = front.textValue;

    // Update back panel UI elements

    // 1. Text toggle (v4.4.3: use toggleFieldVisibility to avoid display:none conflict)
    var textToggleFront = document.getElementById('toggle-website-text-front');
    var textToggleBack = document.getElementById('toggle-website-text-back');
    if (textToggleBack && textToggleFront) {
      textToggleBack.checked = textToggleFront.checked;
      // v4.4.3: Use toggleFieldVisibility instead of direct style.display to prevent toggle bug
      this.toggleFieldVisibility('text-customizer-section-back', textToggleFront.checked);
      this.sideCustomizations.back.textToggleEnabled = textToggleFront.checked;
    }

    // 2. Logo toggle (v4.4.3: use toggleFieldVisibility to avoid display:none conflict)
    var logoToggleFront = document.getElementById('toggle-logo-upload-front');
    var logoToggleBack = document.getElementById('toggle-logo-upload-back');
    if (logoToggleBack && logoToggleFront) {
      logoToggleBack.checked = logoToggleFront.checked;
      // v4.4.3: Use toggleFieldVisibility instead of direct style.display to prevent toggle bug
      this.toggleFieldVisibility('logo-customizer-section-back', logoToggleFront.checked);
      this.sideCustomizations.back.logoToggleEnabled = logoToggleFront.checked;
    }

    // 3. Text input value
    var textInputFront = document.getElementById('text-field-0-front');
    var textInputBack = document.getElementById('text-field-0-back');
    if (textInputBack && textInputFront) {
      textInputBack.value = textInputFront.value;
    }

    // 4. Color swatches - update selected state (v3.15.76: fixed selector from .design-customizer__swatch to .color-swatch)
    if (front.backgroundColor) {
      var backSwatches = document.querySelectorAll('#color-customizer-section-back .color-swatch');
      backSwatches.forEach(function(swatch) {
        var swatchColor = swatch.dataset.color || swatch.style.backgroundColor;
        if (swatchColor && swatchColor.toUpperCase() === front.backgroundColor.toUpperCase()) {
          swatch.classList.add('is-selected');
        } else {
          swatch.classList.remove('is-selected');
        }
      });
      // Also update the custom color button if it was a custom color
      var customBtn = document.querySelector('#color-customizer-section-back .color-swatch--custom');
      if (customBtn && front.backgroundCMYK) {
        customBtn.style.backgroundColor = front.backgroundColor;
        var plusIcon = customBtn.querySelector('.color-swatch__icon');
        if (plusIcon) plusIcon.style.display = 'none';
      }
    } else {
      // Clear any selected swatch on back
      var backSwatches = document.querySelectorAll('#color-customizer-section-back .color-swatch');
      backSwatches.forEach(function(swatch) {
        swatch.classList.remove('is-selected');
      });
    }

    // 5. Logo preview (v4.2.1: handle PDF files with icon instead of image)
    var logoPreviewBack = document.getElementById('logo-preview-back');
    var pdfIconBack = document.getElementById('logo-pdf-icon-back');
    var uploaderBack = document.getElementById('logo-uploader-back');
    var uploadTextBack = uploaderBack ? uploaderBack.querySelector('.logo-uploader__text') : null;
    var removeBtnBack = uploaderBack ? uploaderBack.querySelector('.logo-uploader__remove') : null;
    var logoUrlInputBack = document.getElementById('logo-url-back');

    // v4.2.1: Also copy logoFileName
    back.logoFileName = front.logoFileName;

    if (front.logoUrl) {
      // v4.2.1: Check if it's a PDF by URL or filename
      var isPdf = (front.logoFileName && front.logoFileName.toLowerCase().endsWith('.pdf')) ||
                  (front.logoUrl && front.logoUrl.toLowerCase().endsWith('.pdf'));

      if (isPdf) {
        // Show PDF icon instead of image preview
        if (pdfIconBack) pdfIconBack.style.display = 'flex';
        if (logoPreviewBack) logoPreviewBack.style.display = 'none';
        if (uploaderBack) {
          uploaderBack.classList.add('has-pdf');
          uploaderBack.classList.remove('has-logo');
        }
      } else {
        // Show image preview
        if (logoPreviewBack) {
          logoPreviewBack.src = front.logoUrl;
          logoPreviewBack.style.display = 'block';
        }
        if (pdfIconBack) pdfIconBack.style.display = 'none';
        if (uploaderBack) {
          uploaderBack.classList.add('has-logo');
          uploaderBack.classList.remove('has-pdf');
        }
      }
      if (uploadTextBack) uploadTextBack.style.display = 'none';
      if (removeBtnBack) removeBtnBack.style.display = 'block';
      if (logoUrlInputBack) logoUrlInputBack.value = front.logoUrl;
    } else {
      // Clear back logo (same as removeLogo handler)
      if (logoPreviewBack) {
        logoPreviewBack.src = '';
        logoPreviewBack.style.display = 'none';
      }
      if (pdfIconBack) pdfIconBack.style.display = 'none';
      if (uploadTextBack) uploadTextBack.style.display = '';
      if (removeBtnBack) removeBtnBack.style.display = '';
      if (uploaderBack) {
        uploaderBack.classList.remove('has-logo');
        uploaderBack.classList.remove('has-pdf');
      }
      if (logoUrlInputBack) logoUrlInputBack.value = '';
    }

    // 6. Show brief confirmation
    var copyBtn = document.getElementById('copy-from-front-btn');
    if (copyBtn) {
      var originalText = copyBtn.innerHTML;
      copyBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Copied!';
      copyBtn.classList.add('is-success');
      setTimeout(function() {
        copyBtn.innerHTML = originalText;
        copyBtn.classList.remove('is-success');
      }, 2000);
    }

    log('v3.15.75: Copy from front complete', {
      textToggle: back.textToggleEnabled,
      logoToggle: back.logoToggleEnabled,
      backgroundColor: back.backgroundColor,
      textValue: back.textValue,
      hasLogo: !!back.logoUrl
    });
  };

  /**
   * v3.15.75: Save text and logo to localStorage for cross-product persistence
   * Called on preview to remember customer's customizations
   */
  DesignRadioSelector.prototype.saveToLocalStorage = function() {
    try {
      // Save text from front panel (primary input)
      var textInput = document.getElementById('text-field-0-front');
      var textValue = textInput ? textInput.value.trim() : '';

      if (textValue) {
        localStorage.setItem('popsigns_saved_text', textValue);
        log('v3.15.75: Saved text to localStorage:', textValue);
      } else {
        localStorage.removeItem('popsigns_saved_text');
        log('v3.15.75: Cleared text from localStorage (empty value)');
      }

      // Save logo from front panel (if uploaded)
      var frontLogo = this.sideCustomizations.front.logoUrl;
      var frontLogoFileName = this.sideCustomizations.front.logoFileName;
      if (frontLogo) {
        localStorage.setItem('popsigns_saved_logo_url', frontLogo);
        // v4.2.1: Also save filename to detect PDFs on restore
        if (frontLogoFileName) {
          localStorage.setItem('popsigns_saved_logo_filename', frontLogoFileName);
        }
        log('v4.2.1: Saved logo URL to localStorage:', frontLogo);
      } else {
        localStorage.removeItem('popsigns_saved_logo_url');
        localStorage.removeItem('popsigns_saved_logo_filename');
        log('v3.15.75: Cleared logo from localStorage (no logo)');
      }
    } catch (e) {
      log('v3.15.75: localStorage save error:', e.message);
    }
  };

  /**
   * v3.15.75: Load saved text and logo from localStorage
   * v4.1.3: Also check checkboxes and show sections when data is restored
   * Auto-populates BOTH front and back panels on page load
   */
  DesignRadioSelector.prototype.loadFromLocalStorage = function() {
    var self = this;
    try {
      var savedText = localStorage.getItem('popsigns_saved_text');
      var savedLogoUrl = localStorage.getItem('popsigns_saved_logo_url');
      var hasAutoFilled = false;

      if (savedText) {
        // Apply to BOTH panels
        ['front', 'back'].forEach(function(side) {
          var textInput = document.getElementById('text-field-0-' + side);
          if (textInput && !textInput.value.trim()) {
            textInput.value = savedText;
            self.sideCustomizations[side].textValue = savedText;
            hasAutoFilled = true;

            // v4.4.4: Check the checkbox, show the section, AND update internal state
            var textCheckbox = document.getElementById('toggle-website-text-' + side);
            if (textCheckbox && !textCheckbox.checked) {
              textCheckbox.checked = true;
              self.sideCustomizations[side].textToggleEnabled = true;
              self.toggleFieldVisibility('text-customizer-section-' + side, true);
              log('v4.4.4: Checked text toggle and updated state for', side);
            }
          }
        }, this);
        log('v3.15.75: Restored text from localStorage:', savedText);
      }

      if (savedLogoUrl) {
        // v4.2.1: Also get saved filename to detect PDFs
        var savedLogoFileName = localStorage.getItem('popsigns_saved_logo_filename');
        var isPdf = (savedLogoFileName && savedLogoFileName.toLowerCase().endsWith('.pdf')) ||
                    savedLogoUrl.toLowerCase().endsWith('.pdf');

        // Apply to BOTH panels (same structure as original upload handler)
        ['front', 'back'].forEach(function(side) {
          if (!self.sideCustomizations[side].logoUrl) {
            self.sideCustomizations[side].logoUrl = savedLogoUrl;
            self.sideCustomizations[side].logoFileName = savedLogoFileName;

            // Update UI using correct element IDs
            var logoPreview = document.getElementById('logo-preview-' + side);
            var pdfIcon = document.getElementById('logo-pdf-icon-' + side);
            var uploader = document.getElementById('logo-uploader-' + side);
            var uploadText = uploader ? uploader.querySelector('.logo-uploader__text') : null;
            var removeBtn = uploader ? uploader.querySelector('.logo-uploader__remove') : null;
            var logoUrlInput = document.getElementById('logo-url-' + side);

            // v4.2.1: Handle PDF vs image display
            if (isPdf) {
              if (pdfIcon) pdfIcon.style.display = 'flex';
              if (logoPreview) logoPreview.style.display = 'none';
              if (uploader) {
                uploader.classList.add('has-pdf');
                uploader.classList.remove('has-logo');
              }
            } else {
              if (logoPreview) {
                logoPreview.src = savedLogoUrl;
                logoPreview.style.display = 'block';
              }
              if (pdfIcon) pdfIcon.style.display = 'none';
              if (uploader) {
                uploader.classList.add('has-logo');
                uploader.classList.remove('has-pdf');
              }
            }
            if (uploadText) uploadText.style.display = 'none';
            if (removeBtn) removeBtn.style.display = 'block';
            if (logoUrlInput) logoUrlInput.value = savedLogoUrl;

            hasAutoFilled = true;

            // v4.1.3: Check the checkbox and show the section
            // v4.3.16: Also update sideCustomizations state to match
            var logoCheckbox = document.getElementById('toggle-logo-upload-' + side);
            if (logoCheckbox && !logoCheckbox.checked) {
              logoCheckbox.checked = true;
              self.sideCustomizations[side].logoToggleEnabled = true;
              self.toggleFieldVisibility('logo-customizer-section-' + side, true);
              log('v4.3.16: Checked logo toggle and updated state for', side);
            }
          }
        }, this);
        log('v4.2.1: Restored logo from localStorage (isPdf:', isPdf + ')');
      }

      // Show subtle indicator if values were auto-filled
      if (hasAutoFilled) {
        this.showAutoFillIndicator();
      }
    } catch (e) {
      log('v3.15.75: localStorage load error:', e.message);
    }
  };

  /**
   * v3.15.75: Show brief indicator that values were auto-filled
   */
  DesignRadioSelector.prototype.showAutoFillIndicator = function() {
    // Add a subtle indicator to the customizer panels
    var panels = document.querySelectorAll('.design-customizer--front, .design-customizer--back');
    panels.forEach(function(panel) {
      if (!panel.querySelector('.autofill-indicator')) {
        var indicator = document.createElement('div');
        indicator.className = 'autofill-indicator';
        indicator.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v4"/><path d="m16.2 7.8 2.9-2.9"/><path d="M18 12h4"/><path d="m16.2 16.2 2.9 2.9"/><path d="M12 18v4"/><path d="m4.9 19.1 2.9-2.9"/><path d="M2 12h4"/><path d="m4.9 4.9 2.9 2.9"/></svg> Previous values restored';
        panel.insertBefore(indicator, panel.firstChild);

        // Auto-remove after 5 seconds
        setTimeout(function() {
          if (indicator.parentNode) {
            indicator.classList.add('fade-out');
            setTimeout(function() {
              if (indicator.parentNode) indicator.remove();
            }, 300);
          }
        }, 5000);
      }
    });
  };

  /**
   * Load a design with customizations applied via load-design API
   * @param {Object} cachedDesign - Cached design JSON from DesignTemplateCache
   * @param {Object} customizations - User customizations to apply
   */
  DesignRadioSelector.prototype.loadCustomizedDesign = function(cachedDesign, customizations) {
    var self = this;

    if (!window.DesignModifier) {
      log('ERROR: DesignModifier not available');
      // Fallback to standard swap flow
      this.loadDesignViaSwap(this.frontSelection);
      return;
    }

    // Apply customizations to the cached design JSON
    var modifiedDesign = window.DesignModifier.apply(cachedDesign, customizations);

    log('Loading customized design via SwapController');

    var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();

    if (!swapController) {
      log('ERROR: SwapController not available for customized load');
      // Fallback to standard swap flow
      this.loadDesignViaSwap(this.frontSelection);
      return;
    }

    // Use the SwapController to load the customized design
    this.isLoading = true;
    this.designLoadStartTime = Date.now();
    this.showOverlay('Applying your customizations...');

    // Set up listeners for the load
    this.setupCustomizedLoadListeners(function onComplete() {
      self.onSwapCompleteEditorOpen();
    }, function onError(error) {
      log('Customized load failed:', error);
      self.hideOverlay();
      self.onDesignLoadError({ error: error });
    });

    // Open editor then load customized design
    requestAnimationFrame(function() {
      setTimeout(function() {
        log('Opening editor for customized design load');
        window.ppclient.fire('show-app');
      }, 50);
    });

    // Store the modified design to load after editor opens
    this._pendingCustomizedDesign = modifiedDesign;
    this._pendingCustomizations = customizations;
  };

  /**
   * Set up listeners for customized design loading
   * Loads the pre-modified design JSON directly via load-design event (no swap needed)
   */
  DesignRadioSelector.prototype.setupCustomizedLoadListeners = function(onComplete, onError) {
    var self = this;
    var handled = false;
    var timeoutId = null;

    var editorShownHandler = function() {
      log('Editor shown, loading customized design directly via load-design');
      window.ppclient.off('editor-shown', editorShownHandler);

      // Give editor time to initialize
      setTimeout(function() {
        var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();

        if (swapController && self._pendingCustomizedDesign) {
          // Load the customized design DIRECTLY via load-design event (no swap needed)
          // The design JSON already has customizations applied by DesignModifier
          swapController.loadCustomizedDesign(
            self._pendingCustomizedDesign,
            self._pendingCustomizations,
            self.frontSelection.collectionPage,
            self.currentShape || 'classic'
          ).then(function(result) {
            log('Customized design loaded successfully:', result);
            if (!handled) {
              handled = true;
              clearTimeout(timeoutId);
              self._pendingCustomizedDesign = null;
              self._pendingCustomizations = null;
              // v3.15.28: Clear pending customizations to prevent FALLBACK from running
              self._pendingCustomizationsAfterSwap = null;
              onComplete();
            }
          }).catch(function(error) {
            log('Customized design load error, falling back to swap:', error);
            // Fallback: do a standard swap without customizations
            var pageIndex = self.frontSelection.pageIndex;
            swapController.swapPage(pageIndex, 0).then(function() {
              if (!handled) {
                handled = true;
                clearTimeout(timeoutId);
                onComplete();
              }
            }).catch(function(swapError) {
              if (!handled) {
                handled = true;
                clearTimeout(timeoutId);
                onError(swapError);
              }
            });
          });
        } else {
          log('SwapController or pending design not available');
          if (!handled) {
            handled = true;
            clearTimeout(timeoutId);
            onError('SwapController not available');
          }
        }
      }, 500);
    };

    window.ppclient.on('editor-shown', editorShownHandler);

    // 25 second timeout
    timeoutId = setTimeout(function() {
      if (handled) return;
      handled = true;
      log('Customized load timeout after 25s');
      window.ppclient.off('editor-shown', editorShownHandler);
      onError('Timeout loading customized design');
    }, 25000);
  };

  // ========== v3.14 Collection-Only Loading ==========

  /**
   * Load a design using Page Loader SWAP (v3.14 Collection-Only)
   * This replaces load-design-by-id from v3.13
   */
  DesignRadioSelector.prototype.loadDesignViaSwap = function(template) {
    var self = this;

    if (!window.ppclient || !window.ppclient.fire) {
      log('ppclient not ready, storing as pending selection');
      this.pendingSelection = template;
      return;
    }

    var collectionPage = template.collectionPage;
    var pageIndex = template.pageIndex;

    log('Loading design via collection swap (v3.14):', {
      collectionPage: collectionPage,
      pageIndex: pageIndex,
      title: template.title
    });

    this.isLoading = true;
    this.designLoadStartTime = Date.now();

    this.showOverlay('Loading your design...');

    // Set up listeners for swap-based flow
    this.setupSwapBasedListeners(pageIndex, function onComplete(projectId) {
      self.closeEditorAndFinish(projectId, null, template);
    }, function onError(error) {
      self.hideOverlay();
      self.onDesignLoadError({ error: error });
    });

    // Open the editor
    requestAnimationFrame(function() {
      setTimeout(function() {
        log('Opening editor for collection swap');
        window.ppclient.fire('show-app');
      }, 50);
    });
  };

  /**
   * Set up listeners for swap-based loading (v3.14)
   * Flow: editor-shown → swapPage → leave editor open for user to review
   *
   * NOTE: We do NOT auto-save. The user reviews the design in the editor
   * and clicks submit manually when ready.
   */
  DesignRadioSelector.prototype.setupSwapBasedListeners = function(pageIndex, onComplete, onError) {
    var self = this;
    var handled = false;
    var timeoutId = null;

    log('Setting up swap-based listeners for pageIndex:', pageIndex);

    var editorShownHandler = function() {
      log('Editor shown, initiating swap to pageIndex:', pageIndex);
      window.ppclient.off('editor-shown', editorShownHandler);

      // Give editor time to fully initialize
      setTimeout(function() {
        // v3.15.13: Swap first, then apply customizations via loadCustomizedDesign after swap completes
        self.performCollectionSwap(pageIndex, function(swapResult) {
          log('Collection swap complete:', swapResult);

          // v3.14 UPDATE: Do NOT auto-save. Leave editor open for user to review.
          // Hide overlay and let user see the design in full-screen editor
          if (!handled) {
            handled = true;
            clearTimeout(timeoutId);
            window.ppclient.off('project-saved', projectSavedHandler);

            // Finish loading state - editor stays open
            // Customizations will be applied in onSwapCompleteEditorOpen via loadCustomizedDesign
            self.onSwapCompleteEditorOpen();
          }
        }, function(swapError) {
          log('Collection swap failed:', swapError);
          if (!handled) {
            handled = true;
            clearTimeout(timeoutId);
            onError(swapError);
          }
        });
      }, 500);
    };

    // Listen for project-saved in case user manually saves
    var projectSavedHandler = function(data) {
      if (handled) return;

      var projectId = data?.projectId || data?.data?.projectId ||
                      (window.ppclient && window.ppclient._vars && window.ppclient._vars.projectId);
      log('Project saved by user, projectId:', projectId);

      // User manually saved - now we can complete
      self.onUserSavedProject(projectId);
    };

    window.ppclient.on('editor-shown', editorShownHandler);
    window.ppclient.on('project-saved', projectSavedHandler);

    // 20 second timeout for the swap operation
    timeoutId = setTimeout(function() {
      if (handled) return;
      handled = true;
      log('Swap flow timeout after 20s');
      window.ppclient.off('editor-shown', editorShownHandler);
      window.ppclient.off('project-saved', projectSavedHandler);

      onError('Timeout waiting for design to load');
    }, 20000);
  };

  /**
   * Perform collection swap using SwapController
   * @param {number} pageIndex - Page index to swap
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  DesignRadioSelector.prototype.performCollectionSwap = function(pageIndex, onSuccess, onError) {
    var self = this;

    var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();

    if (!swapController) {
      log('SwapController not available');
      onError('SwapController not available');
      return;
    }

    var waitForReady = function(callback) {
      if (swapController.isReady) {
        callback();
      } else {
        log('Waiting for SwapController...');
        var waitStart = Date.now();
        var checkReady = function() {
          if (swapController.isReady) {
            callback();
          } else if (Date.now() - waitStart > 8000) {
            log('SwapController ready timeout, proceeding anyway');
            callback();
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      }
    };

    waitForReady(function() {
      // v3.15.13: Call swapPage without customizations - they'll be applied after via loadCustomizedDesign
      log('SwapController ready, calling swapPage(' + pageIndex + ', 0)');

      swapController.swapPage(pageIndex, 0).then(function(result) {
        log('swapPage completed:', result);
        onSuccess(result);
      }).catch(function(error) {
        log('swapPage error:', error);
        onError(error);
      });
    });
  };

  // ========== Double-Sided via Swap ==========

  /**
   * Load double-sided design using collection swaps only (v3.14)
   * Flow: swap front (pos 0) → swap back (pos 1) → save
   *
   * NOTE: After the front swap, PitchPrint creates a 2-page design automatically.
   * So we SWAP both pages instead of swap + add.
   */
  DesignRadioSelector.prototype.loadDoubleSidedViaSwap = function() {
    var self = this;

    if (!this.frontSelection || !this.backSelection) {
      log('ERROR: Missing front or back selection');
      return;
    }

    log('Loading double-sided via collection swaps (v3.14):', {
      front: this.frontSelection.title,
      frontCollectionPage: this.frontSelection.collectionPage,
      back: this.backSelection.title,
      backCollectionPage: this.backSelection.collectionPage
    });

    var frontStatus = document.getElementById('design-radio-status-front');
    var backStatus = document.getElementById('design-radio-status-back');
    this.setStatusOnElement(frontStatus, 'loading', 'Loading designs...');
    this.setStatusOnElement(backStatus, 'loading', 'Loading designs...');

    this.setLoadingState(this.frontSelection.radio, true);
    this.setLoadingState(this.backSelection.radio, true);

    this.isLoading = true;
    this.designLoadStartTime = Date.now();

    this.showOverlay('Loading your designs...');

    var frontPageIndex = this.frontSelection.pageIndex;
    var backPageIndex = this.backSelection.pageIndex;

    this.setupDoubleSidedSwapListeners(frontPageIndex, backPageIndex, function onComplete(projectId) {
      self.closeEditorAndFinishDoubleSided(projectId);
    }, function onError(error) {
      self.hideOverlay();
      self.onDesignLoadError({ error: error });
    });

    requestAnimationFrame(function() {
      setTimeout(function() {
        log('Opening editor for double-sided swap');
        window.ppclient.fire('show-app');
      }, 50);
    });
  };

  /**
   * Set up listeners for double-sided swap flow (v3.14)
   * Flow: editor-shown → swap front (pos 0) → swap back (pos 1) → leave editor open
   *
   * NOTE: After front swap, PitchPrint creates 2-page design (app-client-pageCount 2).
   * So we SWAP page 1 instead of trying to ADD (which fails due to limitReached).
   *
   * NOTE: We do NOT auto-save. The user reviews the design in the editor
   * and clicks submit manually when ready.
   */
  DesignRadioSelector.prototype.setupDoubleSidedSwapListeners = function(frontPageIndex, backPageIndex, onComplete, onError) {
    var self = this;
    var handled = false;
    var timeoutId = null;

    log('Setting up double-sided swap listeners: front=' + frontPageIndex + ' (pos 0), back=' + backPageIndex + ' (pos 1)');

    var editorShownHandler = function() {
      log('Editor shown, starting double-sided swap sequence');
      window.ppclient.off('editor-shown', editorShownHandler);

      setTimeout(function() {
        // Step 1: Swap front page (position 0)
        self.performCollectionSwap(frontPageIndex, function(swapResult) {
          log('Front swap complete:', swapResult);

          self.showOverlay('Loading back design...');

          // Step 2: Swap back page (position 1) - use swapPage with targetPosition=1
          // NOTE: After front swap, design has 2 pages, so we swap page 1 instead of adding
          setTimeout(function() {
            self.performCollectionSwapBack(backPageIndex, function(backSwapResult) {
              log('Back swap complete:', backSwapResult);

              // v3.14 UPDATE: Do NOT auto-save. Leave editor open for user to review.
              if (!handled) {
                handled = true;
                clearTimeout(timeoutId);
                window.ppclient.off('project-saved', projectSavedHandler);

                // Finish loading state - editor stays open
                self.onSwapCompleteEditorOpenDoubleSided();
              }
            }, function(backSwapError) {
              log('Back swap failed:', backSwapError);
              if (!handled) {
                handled = true;
                clearTimeout(timeoutId);
                onError(backSwapError);
              }
            });
          }, 500);
        }, function(swapError) {
          log('Front swap failed:', swapError);
          if (!handled) {
            handled = true;
            clearTimeout(timeoutId);
            onError(swapError);
          }
        });
      }, 500);
    };

    // Listen for project-saved in case user manually saves
    var projectSavedHandler = function(data) {
      if (handled) return;

      var projectId = data?.projectId || data?.data?.projectId ||
                      (window.ppclient && window.ppclient._vars && window.ppclient._vars.projectId);
      log('Double-sided project saved by user, projectId:', projectId);

      // User manually saved - now we can complete
      self.onUserSavedProjectDoubleSided(projectId);
    };

    window.ppclient.on('editor-shown', editorShownHandler);
    window.ppclient.on('project-saved', projectSavedHandler);

    // 30 second timeout for the swap operations
    timeoutId = setTimeout(function() {
      if (handled) return;
      handled = true;
      log('Double-sided swap flow timeout after 30s');
      window.ppclient.off('editor-shown', editorShownHandler);
      window.ppclient.off('project-saved', projectSavedHandler);

      onError('Timeout waiting for designs to load');
    }, 30000);
  };

  /**
   * Perform collection add (for back page) using SwapController
   * NOTE: This is now DEPRECATED in v3.14 - use performCollectionSwapBack instead
   */
  DesignRadioSelector.prototype.performCollectionAdd = function(pageIndex, onSuccess, onError) {
    var self = this;

    var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();

    if (!swapController) {
      log('SwapController not available for add');
      onError('SwapController not available');
      return;
    }

    var waitForReady = function(callback) {
      if (swapController.isReady) {
        callback();
      } else {
        log('Waiting for SwapController (add)...');
        var waitStart = Date.now();
        var checkReady = function() {
          if (swapController.isReady) {
            callback();
          } else if (Date.now() - waitStart > 5000) {
            log('SwapController ready timeout, proceeding anyway');
            callback();
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      }
    };

    waitForReady(function() {
      log('SwapController ready, calling addPage(' + pageIndex + ')');

      swapController.addPage(pageIndex).then(function(result) {
        log('addPage completed:', result);
        onSuccess(result);
      }).catch(function(error) {
        log('addPage error:', error);
        onError(error);
      });
    });
  };

  /**
   * Perform collection swap for BACK page (position 1)
   * v3.14: After front swap creates 2-page design, we SWAP page 1 instead of ADD
   */
  DesignRadioSelector.prototype.performCollectionSwapBack = function(pageIndex, onSuccess, onError) {
    var self = this;

    var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();

    if (!swapController) {
      log('SwapController not available for back swap');
      onError('SwapController not available');
      return;
    }

    var waitForReady = function(callback) {
      if (swapController.isReady) {
        callback();
      } else {
        log('Waiting for SwapController (back swap)...');
        var waitStart = Date.now();
        var checkReady = function() {
          if (swapController.isReady) {
            callback();
          } else if (Date.now() - waitStart > 5000) {
            log('SwapController ready timeout, proceeding anyway');
            callback();
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      }
    };

    waitForReady(function() {
      log('SwapController ready, calling swapPage(' + pageIndex + ', 1) for back page');

      // Use swapPage with targetPosition=1 for the back page
      swapController.swapPage(pageIndex, 1).then(function(result) {
        log('Back swapPage completed:', result);
        onSuccess(result);
      }).catch(function(error) {
        log('Back swapPage error:', error);
        onError(error);
      });
    });
  };

  // ========== Save and Finish ==========

  DesignRadioSelector.prototype.triggerSaveViaSwapController = function(onSuccess, onError) {
    var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();

    if (!swapController) {
      log('SwapController not available for save');
      onError('SwapController not available');
      return;
    }

    log('Waiting for SwapController to save...');

    var waitStartTime = Date.now();
    var maxWaitTime = 5000;

    var checkReady = function() {
      if (swapController.isReady) {
        log('SwapController ready, triggering save');
        swapController.saveProject().then(function(result) {
          log('saveProject resolved:', result);
          onSuccess(result);
        }).catch(function(error) {
          log('saveProject rejected:', error);
          onError(error);
        });
      } else if (Date.now() - waitStartTime > maxWaitTime) {
        log('SwapController wait timeout, proceeding anyway');
        swapController.saveProject().then(onSuccess).catch(onError);
      } else {
        setTimeout(checkReady, 100);
      }
    };

    checkReady();
  };

  /**
   * Called when swap completes - editor stays open for user to review (single-sided)
   * User will manually click submit in the editor when ready
   * v3.15.1: Apply pending customizations directly via modify-canvas (correct design is now loaded)
   * v3.15.18: Customizations are now applied DURING swap (before Page Loader closes), so this
   *           fallback path should rarely be needed. If customizations were applied during swap,
   *           _pendingCustomizationsAfterSwap will be null.
   */
  DesignRadioSelector.prototype.onSwapCompleteEditorOpen = function() {
    var self = this;

    log('Swap complete - editor staying open for user review');

    // v3.15.18: Check if customizations are still pending (they should be null if applied during swap)
    var pendingCustomizations = this._pendingCustomizationsAfterSwap;
    var hasCustomizations = pendingCustomizations && this.hasCustomizations(pendingCustomizations);

    if (hasCustomizations) {
      // v3.15.18: This is now a FALLBACK path - customizations should have been applied during swap
      log('v3.15.18 FALLBACK: Customizations not applied during swap, trying post-swap application...');
      this.showOverlay('Applying your customizations...');
    } else {
      log('v3.15.18: No pending customizations (already applied during swap or none requested)');
    }

    // Reset loading states
    this.isLoading = false;
    if (this.frontSelection && this.frontSelection.radio) {
      this.setLoadingState(this.frontSelection.radio, false);
    }

    // Update status to indicate design is loaded
    var statusEl = document.getElementById('design-radio-status-front') || this.statusEl;
    this.setStatusOnElement(statusEl, 'ready', '✓ Design loaded - review in editor');

    // Reset Preview button (it will show again if user changes selection after closing editor)
    this.setPreviewButtonLoading(false);
    this.hidePreviewButton();

    // v3.15.1: Apply pending customizations directly via modify-canvas
    // The correct design is now loaded, so we can safely modify it
    if (hasCustomizations) {
      var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();
      if (swapController) {
        var collectionPage = this.frontSelection.collectionPage;
        var shape = this.currentShape || 'classic';

        // Send customizations directly to modify-canvas (design is already loaded)
        swapController.loadCustomizedDesign(
          null, // designJson not needed - we're modifying current canvas
          pendingCustomizations,
          collectionPage,
          shape
        ).then(function(result) {
          log('Customizations applied successfully:', result);
          self._pendingCustomizationsAfterSwap = null;
          self.hideOverlay();
        }).catch(function(err) {
          log('Failed to apply customizations (non-critical):', err);
          self._pendingCustomizationsAfterSwap = null;
          self.hideOverlay();
        });
      } else {
        log('SwapController not available for customizations');
        this._pendingCustomizationsAfterSwap = null;
        this.hideOverlay();
      }
    } else {
      // No customizations - hide overlay normally
      this.hideOverlay();
    }

    // v3.15.14: Cache the design for future use (after swap completes with correct template)
    // Include template title for validation to prevent wrong template being cached/returned
    if (window.DesignTemplateCache && this.frontSelection) {
      var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();
      if (swapController && swapController.extractDesignCache) {
        var collectionPage = this.frontSelection.collectionPage;
        var shape = this.currentShape || 'classic';
        var templateTitle = this.frontSelection.title;

        // Request design extraction from the iframe (cache the base design without customizations)
        swapController.extractDesignCache(collectionPage, shape).then(function(result) {
          if (result && result.designJson) {
            // v3.15.14: Include title for cache validation
            window.DesignTemplateCache.set(collectionPage, shape, result.designJson, templateTitle);
            log('Design cached for future customization:', collectionPage, shape, templateTitle, result.designJson.pages.length, 'pages');

            // v4.3.26: Clear retry timeout if active (cache successfully built)
            if (self._previewRetryTimeout) {
              clearTimeout(self._previewRetryTimeout);
              self._previewRetryTimeout = null;
              self._activeRetryContext = null;
              log('v4.3.26: Cache built successfully - retry timeout cleared');
            }
          } else {
            log('Design extraction returned no data:', result);
          }
        }).catch(function(err) {
          log('Design cache extraction failed (non-critical):', err);
        });
      }
    }

    var totalElapsed = Date.now() - this.designLoadStartTime;
    log('Swap completed in', totalElapsed, 'ms - editor is open for user review');
  };

  /**
   * v4.3.24: Warmup - Pre-cache the default design on mobile by silently opening the editor
   * This ensures the Preview button works on the first attempt without crashes.
   *
   * Flow:
   * 1. Only runs on mobile (where crashes occur)
   * 2. Only runs if no cache exists for collectionPage 1
   * 3. Opens editor silently (it's already hidden via CSS)
   * 4. Triggers a swap to load collectionPage 1
   * 5. Extracts and caches the design
   * 6. Closes the editor
   */
  DesignRadioSelector.prototype.preCacheDefaultDesign = function() {
    var self = this;
    var isMobile = window.innerWidth < 768;

    // v4.3.26: DISABLED - warmup blocks page interaction for ~10s, poor UX
    // Using retry mechanism instead (see handlePreviewButtonClick)
    if (isMobile) {
      log('v4.3.26: Mobile warmup disabled - using retry mechanism instead');
      return;
    }

    // v4.3.24: Only warmup on desktop (safe, doesn't crash)
    if (!isMobile) {
      log('v4.3.24: Desktop detected - skipping warmup (not needed)');
      return;
    }

    var shape = this.currentShape || 'classic';
    var collectionPage = 1; // Default design is always collectionPage 1

    // Check if we already have this cached
    if (window.DesignTemplateCache && window.DesignTemplateCache.has(collectionPage, shape)) {
      log('v4.3.24: Default design already cached for', shape, '- skipping warmup');
      return;
    }

    // Find the first visible template radio for this shape to get the title
    var firstRadio = this.container.querySelector(
      'input[type="radio"][name="design_template_front"][data-collection-page="1"]' +
      (shape !== 'classic' ? '[data-shape="' + shape + '"]' : ':not([data-shape])')
    );

    // Fallback: try without shape filter if not found
    if (!firstRadio) {
      firstRadio = this.container.querySelector(
        'input[type="radio"][name="design_template_front"][data-collection-page="1"]'
      );
    }

    var templateTitle = firstRadio ? firstRadio.dataset.title : null;

    if (!templateTitle) {
      log('v4.3.24: Could not find first template title - skipping warmup');
      return;
    }

    if (!window.ppclient || !window.ppclient.fire) {
      log('v4.3.24: ppclient not ready - skipping warmup');
      return;
    }

    var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();
    if (!swapController) {
      log('v4.3.24: SwapController not available - skipping warmup');
      return;
    }

    log('v4.3.24: Starting mobile warmup for', shape, collectionPage, templateTitle);
    this._warmupInProgress = true;

    // v4.3.24: Save scroll position and lock body scroll to prevent page jump
    this._warmupScrollY = window.scrollY || window.pageYOffset || 0;
    this._warmupBodyOverflow = document.body.style.overflow;
    this._warmupBodyPosition = document.body.style.position;
    this._warmupBodyTop = document.body.style.top;
    this._warmupBodyWidth = document.body.style.width;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + this._warmupScrollY + 'px';
    document.body.style.width = '100%';
    log('v4.3.24: Scroll locked at position:', this._warmupScrollY);

    // v4.3.24: Force-hide the iframe during warmup to prevent flash
    var ppIframe = document.querySelector('iframe[src*="pitchprint"]');
    if (ppIframe) {
      this._warmupOriginalStyles = {
        visibility: ppIframe.style.visibility,
        opacity: ppIframe.style.opacity,
        pointerEvents: ppIframe.style.pointerEvents
      };
      ppIframe.style.visibility = 'hidden';
      ppIframe.style.opacity = '0';
      ppIframe.style.pointerEvents = 'none';
      log('v4.3.24: Iframe hidden for warmup');
    }

    // Listen for editor-shown, then do the swap
    var warmupEditorHandler = function() {
      log('v4.3.24: Warmup - editor shown, starting swap');
      window.ppclient.off('editor-shown', warmupEditorHandler);

      // Small delay to let editor initialize
      setTimeout(function() {
        // Do the swap to collectionPage 1
        swapController.swapPage(0, 0).then(function(swapResult) {
          log('v4.3.24: Warmup swap complete:', swapResult);

          // Extract and cache the design
          swapController.extractDesignCache(collectionPage, shape).then(function(result) {
            if (result && result.designJson) {
              window.DesignTemplateCache.set(collectionPage, shape, result.designJson, templateTitle);
              log('v4.3.24: Warmup cache built:', shape, collectionPage, templateTitle,
                  result.designJson.pages.length, 'pages');
            } else {
              log('v4.3.24: Warmup extraction returned no data');
            }

            // Close the editor
            self._closeWarmupEditor();
          }).catch(function(err) {
            log('v4.3.24: Warmup extraction failed:', err);
            self._closeWarmupEditor();
          });
        }).catch(function(err) {
          log('v4.3.24: Warmup swap failed:', err);
          self._closeWarmupEditor();
        });
      }, 500);
    };

    window.ppclient.on('editor-shown', warmupEditorHandler);

    // Safety timeout - close editor if warmup takes too long
    this._warmupTimeout = setTimeout(function() {
      log('v4.3.24: Warmup timeout - closing editor');
      window.ppclient.off('editor-shown', warmupEditorHandler);
      self._closeWarmupEditor();
    }, 15000);

    // Open the editor silently (it's hidden via CSS transforms)
    log('v4.3.24: Opening editor for warmup');
    window.ppclient.fire('show-app');
  };

  /**
   * v4.3.24: Close editor after warmup completes and restore iframe visibility + scroll position
   */
  DesignRadioSelector.prototype._closeWarmupEditor = function() {
    if (this._warmupTimeout) {
      clearTimeout(this._warmupTimeout);
      this._warmupTimeout = null;
    }

    // Restore iframe visibility
    if (this._warmupOriginalStyles) {
      var ppIframe = document.querySelector('iframe[src*="pitchprint"]');
      if (ppIframe) {
        ppIframe.style.visibility = this._warmupOriginalStyles.visibility || '';
        ppIframe.style.opacity = this._warmupOriginalStyles.opacity || '';
        ppIframe.style.pointerEvents = this._warmupOriginalStyles.pointerEvents || '';
        log('v4.3.24: Iframe visibility restored');
      }
      this._warmupOriginalStyles = null;
    }

    // Restore scroll position and unlock body scroll
    if (typeof this._warmupScrollY !== 'undefined') {
      document.body.style.overflow = this._warmupBodyOverflow || '';
      document.body.style.position = this._warmupBodyPosition || '';
      document.body.style.top = this._warmupBodyTop || '';
      document.body.style.width = this._warmupBodyWidth || '';
      window.scrollTo(0, this._warmupScrollY);
      log('v4.3.24: Scroll unlocked and restored to position:', this._warmupScrollY);

      this._warmupScrollY = undefined;
      this._warmupBodyOverflow = undefined;
      this._warmupBodyPosition = undefined;
      this._warmupBodyTop = undefined;
      this._warmupBodyWidth = undefined;
    }

    if (this._warmupInProgress) {
      log('v4.3.24: Closing editor after warmup');
      this._warmupInProgress = false;
      window.ppclient.fire('close-app');
    }
  };

  /**
   * v4.4.0: On-click cache warmup for mobile first-preview crash prevention
   * Performs silent warmup when user clicks Preview button (instead of page load)
   * Returns Promise that resolves when cache built, rejects on timeout/error
   */
  DesignRadioSelector.prototype.performOnClickWarmup = function(collectionPage, shape, templateTitle) {
    var self = this;

    return new Promise(function(resolve, reject) {
      log('v4.4.0: Starting on-click warmup for', shape, collectionPage, templateTitle);

      // Validation checks
      if (!window.ppclient || !window.ppclient.fire) {
        log('v4.4.0: ppclient not ready - cannot warmup');
        return reject('ppclient not ready');
      }

      var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();
      if (!swapController) {
        log('v4.4.0: SwapController not available - cannot warmup');
        return reject('SwapController not available');
      }

      // Set warmup flag (prevents conflicts with user actions)
      self._warmupInProgress = true;

      // Hide iframe to prevent flash (user sees loading overlay instead)
      var ppIframe = document.querySelector('iframe[src*="pitchprint"]');
      if (ppIframe) {
        self._warmupOriginalStyles = {
          visibility: ppIframe.style.visibility,
          opacity: ppIframe.style.opacity,
          pointerEvents: ppIframe.style.pointerEvents
        };
        ppIframe.style.visibility = 'hidden';
        ppIframe.style.opacity = '0';
        ppIframe.style.pointerEvents = 'none';
        log('v4.4.0: Iframe hidden for warmup');
      }

      // Setup editor-shown listener
      var warmupEditorHandler = function() {
        log('v4.4.0: Warmup - editor shown, starting swap');
        window.ppclient.off('editor-shown', warmupEditorHandler);

        // Small delay to let editor initialize
        setTimeout(function() {
          // Calculate pageIndex from collectionPage (collectionPage is 1-indexed)
          var pageIndex = collectionPage - 1;

          // Perform swap
          swapController.swapPage(pageIndex, 0).then(function(swapResult) {
            log('v4.4.0: Warmup swap complete:', swapResult);

            // Extract and cache the design
            swapController.extractDesignCache(collectionPage, shape).then(function(result) {
              if (result && result.designJson) {
                window.DesignTemplateCache.set(collectionPage, shape, result.designJson, templateTitle);
                log('v4.4.0: Warmup cache built successfully:', shape, collectionPage, templateTitle,
                    result.designJson.pages.length, 'pages');

                // Close editor and restore state
                self._closeWarmupEditor();

                // Resolve promise - cache is ready
                resolve(true);
              } else {
                log('v4.4.0: Warmup extraction returned no data');
                self._closeWarmupEditor();
                reject('Cache extraction failed - no data');
              }
            }).catch(function(err) {
              log('v4.4.0: Warmup extraction failed:', err);
              self._closeWarmupEditor();
              reject(err);
            });
          }).catch(function(err) {
            log('v4.4.0: Warmup swap failed:', err);
            self._closeWarmupEditor();
            reject(err);
          });
        }, 500);
      };

      window.ppclient.on('editor-shown', warmupEditorHandler);

      // Setup timeout (15s)
      self._warmupTimeout = setTimeout(function() {
        log('v4.4.0: Warmup timeout after 15s - aborting');
        window.ppclient.off('editor-shown', warmupEditorHandler);
        self._closeWarmupEditor();
        reject('Warmup timeout');
      }, 15000);

      // Open editor silently (hidden behind overlay)
      log('v4.4.0: Opening editor for warmup');
      window.ppclient.fire('show-app');
    });
  };

  /**
   * v4.3.26: Attempt to retry preview after timeout (mobile first-preview crash recovery)
   */
  DesignRadioSelector.prototype._attemptPreviewRetry = function(context) {
    var self = this;
    this._previewRetryTimeout = null;

    log('v4.3.26: Preview timeout reached - checking if cache was built');

    // Check if cache was built during the failed attempt
    var cachedDesign = window.DesignTemplateCache &&
                       window.DesignTemplateCache.get(context.collectionPage, context.shape, context.templateTitle);

    if (cachedDesign) {
      log('v4.3.26: Cache found! Retrying preview with cached design');

      // Close any open editor from failed attempt
      if (window.ppclient && window.ppclient.fire) {
        window.ppclient.fire('close-app');
      }

      // Small delay to let editor close
      setTimeout(function() {
        // Reset loading state
        self.isLoading = false;
        self.setPreviewButtonLoading(false);
        if (context.selection.radio) {
          self.setLoadingState(context.selection.radio, false);
        }

        // Retry using cached design
        log('v4.3.26: Retrying preview (attempt 2 of 2)');
        self._pendingCustomizationsAfterSwap = context.customizations;

        if (context.selection.radio) {
          self.setLoadingState(context.selection.radio, true);
        }
        self.setPreviewButtonLoading(true);

        self.loadCustomizedDesign(cachedDesign, context.customizations);
      }, 500);
    } else {
      log('v4.3.26: No cache found after timeout - preview failed');
      // Reset loading state
      self.isLoading = false;
      self.setPreviewButtonLoading(false);
      if (context.selection.radio) {
        self.setLoadingState(context.selection.radio, false);
      }

      // Show error message
      alert('Preview timeout. Please try again.');
    }
  };

  /**
   * Called when swap completes - editor stays open for user to review (double-sided)
   * User will manually click submit in the editor when ready
   * v3.15.51: Now applies pending customizations to both pages before finishing
   */
  DesignRadioSelector.prototype.onSwapCompleteEditorOpenDoubleSided = function() {
    var self = this;

    log('Double-sided swap complete - checking for pending customizations');

    var pendingCustomizations = this._pendingDoubleSidedCustomizations;
    var hasCustomizations = pendingCustomizations && this.hasCustomizations(pendingCustomizations);

    if (hasCustomizations) {
      log('v3.15.51: Applying customizations to double-sided design');

      // Show overlay while applying customizations
      this.showOverlay('Applying customizations...');

      var swapController = window.getPitchPrintSwapController && window.getPitchPrintSwapController();

      if (swapController && typeof swapController.loadCustomizedDesignDoubleSided === 'function') {
        // v3.15.51: Apply customizations to BOTH pages via SwapController
        swapController.loadCustomizedDesignDoubleSided(
          pendingCustomizations,
          this.frontSelection ? this.frontSelection.collectionPage : null,
          this.currentShape || 'classic'
        ).then(function(result) {
          log('v3.15.51: Double-sided customizations applied:', result);
          self._pendingDoubleSidedCustomizations = null;
          self._finishDoubleSidedLoad();
        }).catch(function(err) {
          log('v3.15.51: Failed to apply customizations (non-critical):', err);
          self._pendingDoubleSidedCustomizations = null;
          self._finishDoubleSidedLoad();
        });
      } else {
        log('v3.15.51: SwapController.loadCustomizedDesignDoubleSided not available');
        this._pendingDoubleSidedCustomizations = null;
        this._finishDoubleSidedLoad();
      }
    } else {
      // No customizations - finish immediately
      log('v3.15.51: No customizations to apply, finishing double-sided load');
      this._finishDoubleSidedLoad();
    }
  };

  /**
   * v3.15.51: Helper function to finish double-sided load after swaps and customizations
   */
  DesignRadioSelector.prototype._finishDoubleSidedLoad = function() {
    log('v3.15.51: Finishing double-sided load');

    // Hide overlay - user can now see the editor with both designs
    this.hideOverlay();

    // Reset loading states
    this.isLoading = false;
    if (this.frontSelection && this.frontSelection.radio) {
      this.setLoadingState(this.frontSelection.radio, false);
    }
    if (this.backSelection && this.backSelection.radio) {
      this.setLoadingState(this.backSelection.radio, false);
    }

    // Update status to indicate designs are loaded
    var frontStatus = document.getElementById('design-radio-status-front');
    var backStatus = document.getElementById('design-radio-status-back');
    this.setStatusOnElement(frontStatus, 'ready', '✓ ' + (this.frontSelection ? this.frontSelection.title : 'Loaded'));
    this.setStatusOnElement(backStatus, 'ready', '✓ ' + (this.backSelection ? this.backSelection.title : 'Loaded'));

    // Reset Preview button
    this.setPreviewButtonLoading(false);
    this.hidePreviewButton();

    var totalElapsed = Date.now() - this.designLoadStartTime;
    log('v3.15.51: Double-sided load completed in', totalElapsed, 'ms - editor is open for user review');
  };

  /**
   * Called when user manually saves the project in the editor (single-sided)
   * This completes the flow - update localStorage and form fields
   */
  DesignRadioSelector.prototype.onUserSavedProject = function(projectId) {
    var self = this;
    var template = this.selectedTemplate || this.frontSelection;

    log('User saved project:', projectId);

    if (projectId) {
      this.saveProjectToLocalStorage(projectId);
    }

    if (projectId && window.ppclient && window.ppclient._vars) {
      window.ppclient._vars.projectId = projectId;
    }

    this.updateFormFields(projectId, template ? template.title : '');

    var ppInput = document.querySelector('input[name="properties[_pitchprint]"]');
    if (ppInput && projectId) {
      ppInput.value = projectId;
    }

    var statusEl = document.getElementById('design-radio-status-front') || this.statusEl;
    this.setStatusOnElement(statusEl, 'ready', '✓ Design saved: ' + (template ? template.title : 'Custom'));

    this.notifyStateManager({
      projectId: projectId,
      designId: null,
      title: template ? template.title : '',
      previewUrl: template ? template.previewUrl : null
    });

    if (template && template.previewUrl) {
      this.updateGalleryPreview(template.previewUrl);
    }

    log('Single-sided design saved and completed');
  };

  /**
   * Called when user manually saves the project in the editor (double-sided)
   * This completes the flow - update localStorage and form fields
   */
  DesignRadioSelector.prototype.onUserSavedProjectDoubleSided = function(projectId) {
    var self = this;

    log('User saved double-sided project:', projectId);

    if (projectId) {
      this.saveProjectToLocalStorage(projectId);
    }

    if (projectId && window.ppclient && window.ppclient._vars) {
      window.ppclient._vars.projectId = projectId;
    }

    var combinedTitle = (this.frontSelection ? this.frontSelection.title : '') +
                        ' / ' +
                        (this.backSelection ? this.backSelection.title : '');
    this.updateFormFields(projectId, combinedTitle);

    var ppInput = document.querySelector('input[name="properties[_pitchprint]"]');
    if (ppInput && projectId) {
      ppInput.value = projectId;
    }

    var frontStatus = document.getElementById('design-radio-status-front');
    var backStatus = document.getElementById('design-radio-status-back');
    this.setStatusOnElement(frontStatus, 'ready', '✓ Saved: ' + (this.frontSelection ? this.frontSelection.title : ''));
    this.setStatusOnElement(backStatus, 'ready', '✓ Saved: ' + (this.backSelection ? this.backSelection.title : ''));

    this.notifyStateManager({
      projectId: projectId,
      designId: null,
      title: combinedTitle,
      previewUrl: this.frontSelection ? this.frontSelection.previewUrl : null,
      isDoubleSided: true,
      numPages: 2
    });

    if (this.frontSelection && this.frontSelection.previewUrl) {
      this.updateGalleryPreview(this.frontSelection.previewUrl);
    }

    var event = new CustomEvent('pitchprint-double-sided-saved', {
      detail: {
        projectId: projectId,
        frontDesign: this.frontSelection,
        backDesign: this.backSelection
      }
    });
    document.dispatchEvent(event);

    log('Double-sided design saved and completed');
  };

  DesignRadioSelector.prototype.closeEditorAndFinish = function(projectId, designId, template) {
    var self = this;

    log('Closing editor');
    window.ppclient.fire('close-app');

    setTimeout(function() {
      self.hideOverlay();

      var totalElapsed = Date.now() - self.designLoadStartTime;
      log('Collection swap load-save complete. Duration:', totalElapsed, 'ms, projectId:', projectId);

      self.onProjectSaved({
        projectId: projectId,
        designId: designId
      });

      // Hide Preview button after successful load
      self.hidePreviewButton();
      self.setPreviewButtonLoading(false);
    }, 300);
  };

  DesignRadioSelector.prototype.closeEditorAndFinishDoubleSided = function(projectId) {
    var self = this;

    log('Closing editor (double-sided)');
    window.ppclient.fire('close-app');

    setTimeout(function() {
      self.hideOverlay();

      var totalElapsed = Date.now() - self.designLoadStartTime;
      log('Double-sided swap complete. Duration:', totalElapsed, 'ms, projectId:', projectId);

      self.isLoading = false;
      if (self.frontSelection && self.frontSelection.radio) {
        self.setLoadingState(self.frontSelection.radio, false);
      }
      if (self.backSelection && self.backSelection.radio) {
        self.setLoadingState(self.backSelection.radio, false);
      }

      var frontStatus = document.getElementById('design-radio-status-front');
      var backStatus = document.getElementById('design-radio-status-back');
      self.setStatusOnElement(frontStatus, 'ready', '✓ ' + (self.frontSelection ? self.frontSelection.title : 'Saved'));
      self.setStatusOnElement(backStatus, 'ready', '✓ ' + (self.backSelection ? self.backSelection.title : 'Saved'));

      if (projectId) {
        self.saveProjectToLocalStorage(projectId);
      }

      if (projectId && window.ppclient && window.ppclient._vars) {
        window.ppclient._vars.projectId = projectId;
      }

      var combinedTitle = (self.frontSelection ? self.frontSelection.title : '') +
                          ' / ' +
                          (self.backSelection ? self.backSelection.title : '');
      self.updateFormFields(projectId, combinedTitle);

      var ppInput = document.querySelector('input[name="properties[_pitchprint]"]');
      if (ppInput && projectId) {
        ppInput.value = projectId;
      }

      self.notifyStateManager({
        projectId: projectId,
        designId: null,
        title: combinedTitle,
        previewUrl: self.frontSelection ? self.frontSelection.previewUrl : null,
        isDoubleSided: true,
        numPages: 2
      });

      if (self.frontSelection && self.frontSelection.previewUrl) {
        self.updateGalleryPreview(self.frontSelection.previewUrl);
      }

      var event = new CustomEvent('pitchprint-double-sided-saved', {
        detail: {
          projectId: projectId,
          frontDesign: self.frontSelection,
          backDesign: self.backSelection
        }
      });
      document.dispatchEvent(event);

      // Hide Preview button after successful load
      self.hidePreviewButton();
      self.setPreviewButtonLoading(false);

      log('Double-sided design selection complete');
    }, 300);
  };

  // ========== Overlay ==========

  DesignRadioSelector.prototype.showOverlay = function(message) {
    var overlay = document.getElementById('design-radio-overlay');
    var textEl = overlay && overlay.querySelector('.design-radio-overlay__text');
    if (overlay) {
      if (textEl && message) textEl.textContent = message;
      overlay.style.display = 'flex';
    }
  };

  DesignRadioSelector.prototype.hideOverlay = function() {
    var overlay = document.getElementById('design-radio-overlay');
    if (overlay) overlay.style.display = 'none';
  };

  // ========== localStorage ==========

  DesignRadioSelector.prototype.getCurrentVariantId = function() {
    try {
      var urlParams = new URLSearchParams(window.location.search);
      var variantFromUrl = urlParams.get('variant');
      if (variantFromUrl) return variantFromUrl;
    } catch (e) {}

    var variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) return variantInput.value;

    var variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect && variantSelect.value) return variantSelect.value;

    return null;
  };

  DesignRadioSelector.prototype.saveProjectToLocalStorage = function(projectId) {
    if (!projectId) return;

    var productId = this.config.productId ||
                    (this.container && this.container.dataset.productId);

    if (!productId) {
      log('ERROR: No product ID found');
      return;
    }

    var variantId = this.getCurrentVariantId();
    var productName = document.querySelector('.product__title h1')?.textContent?.trim() || 'Product';

    var variantOptions = [];
    var activeCards = document.querySelectorAll('.product-option-card.active, .variant-card.active');
    activeCards.forEach(function(card) {
      var optionValue = card.querySelector('.card-title, .option-title')?.textContent?.trim();
      if (optionValue) variantOptions.push(optionValue);
    });

    var projectData = {
      projectId: projectId,
      numPages: this.isDoubleSidedMode ? 2 : 1,
      meta: {},
      userId: 'guest',
      product: {
        id: productId,
        name: productName.toLowerCase(),
        title: productName
      },
      shopifyVariant: {
        id: variantId,
        options: variantOptions
      }
    };

    var encodedData = encodeURIComponent(JSON.stringify(projectData));

    log('Saving to localStorage:', { projectId: projectId, productId: productId });

    try {
      var pprintProjects = JSON.parse(localStorage.getItem('pprint-projects') || '{}');
      var pprintSp = JSON.parse(localStorage.getItem('pprint-sp') || '{}');
      var ppW2pProjects = JSON.parse(localStorage.getItem('pp_w2p_projects') || '{}');

      pprintProjects[projectId] = encodedData;
      pprintSp[productId] = encodedData;
      ppW2pProjects[productId] = encodedData;

      localStorage.setItem('pprint-projects', JSON.stringify(pprintProjects));
      localStorage.setItem('pprint-sp', JSON.stringify(pprintSp));
      localStorage.setItem('pp_w2p_projects', JSON.stringify(ppW2pProjects));

      log('✅ Saved to all localStorage keys');
    } catch (e) {
      log('ERROR saving to localStorage:', e);
    }
  };

  // ========== Project Saved Handler ==========

  DesignRadioSelector.prototype.onProjectSaved = function(data) {
    log('Project saved:', data);

    clearTimeout(this.loadTimeout);
    this.isLoading = false;

    var template = this.selectedTemplate;
    if (!template) return;

    var projectId = data.projectId ||
                    (window.ppclient && window.ppclient._vars && window.ppclient._vars.projectId);

    if (projectId) {
      this.saveProjectToLocalStorage(projectId);
    }

    if (projectId && window.ppclient && window.ppclient._vars) {
      window.ppclient._vars.projectId = projectId;
    }

    this.updateFormFields(projectId, template.title);

    var ppInput = document.querySelector('input[name="properties[_pitchprint]"]');
    if (ppInput && projectId) {
      ppInput.value = projectId;
    }

    var statusEl = document.getElementById('design-radio-status-' + template.side) || this.statusEl;
    var statusMessage = projectId
      ? 'Design saved: ' + template.title
      : 'Design selected: ' + template.title;
    this.setStatusOnElement(statusEl, 'ready', statusMessage);
    this.setLoadingState(template.radio, false);

    this.notifyStateManager({
      projectId: projectId,
      designId: null,
      title: template.title,
      previewUrl: template.previewUrl
    });

    this.updateGalleryPreview(template.previewUrl);

    log('Design selection complete');
  };

  DesignRadioSelector.prototype.onDesignLoadError = function(data) {
    log('Design load failed:', data);

    clearTimeout(this.loadTimeout);
    this.isLoading = false;

    var template = this.selectedTemplate;
    if (!template) return;

    var statusEl = document.getElementById('design-radio-status-' + template.side) || this.statusEl;

    // v3.15.28: Check if error requires page refresh (PitchPrint broken state after cancel)
    var errorData = data && data.error ? data.error : data;
    var needsRefresh = errorData && errorData.needsRefresh;
    var errorMessage = needsRefresh
      ? 'Editor needs refresh. Reloading page...'
      : 'Failed to load design. Please try again.';

    this.setStatusOnElement(statusEl, 'error', errorMessage);
    this.setLoadingState(template.radio, false);

    // Reset Preview button state on error (don't hide - let user retry)
    this.setPreviewButtonLoading(false);
    this.updatePreviewButtonState();

    template.radio.checked = false;

    // v3.15.28: If PitchPrint is in a broken state, refresh the page
    if (needsRefresh) {
      log('PitchPrint needs refresh - reloading page in 2 seconds');
      setTimeout(function() {
        window.location.reload();
      }, 2000);
    }
  };

  // ========== Helpers ==========

  DesignRadioSelector.prototype.updateSideSelectionInput = function(side, collectionPage, title, designId) {
    var inputId = side === 'back' ? 'design-radio-back-selection' : 'design-radio-front-selection';
    var input = document.getElementById(inputId);
    if (input) {
      input.value = JSON.stringify({
        collectionPage: collectionPage,
        title: title,
        designId: designId || null
      });
    }
  };

  DesignRadioSelector.prototype.updateFormFields = function(projectId, title) {
    var selectedInput = document.getElementById('design-radio-selected');
    var titleInput = document.getElementById('design-radio-title');

    if (selectedInput) selectedInput.value = projectId || '';
    if (titleInput) titleInput.value = title || '';
  };

  DesignRadioSelector.prototype.notifyStateManager = function(data) {
    if (!window.PitchPrintStateManager) return;

    try {
      window.PitchPrintStateManager.setProject({
        projectId: data.projectId,
        source: 'design-radio-selector-v314',
        designId: data.designId,
        previewUrl: data.previewUrl
      });

      window.PitchPrintStateManager.emit('design:radio:changed', {
        designId: data.designId,
        title: data.title,
        projectId: data.projectId,
        previewUrl: data.previewUrl
      });
    } catch (err) {
      log('Error notifying StateManager:', err);
    }
  };

  DesignRadioSelector.prototype.updateGalleryPreview = function(previewUrl) {
    if (!previewUrl) return;

    var previewElements = document.querySelectorAll(
      '.design-preview-thumbnail, .design-radio-preview-result, [data-design-preview]'
    );

    previewElements.forEach(function(el) {
      if (el.tagName === 'IMG') {
        el.src = previewUrl;
      } else {
        el.style.backgroundImage = 'url(' + previewUrl + ')';
      }
    });

    if (typeof window.refreshGalleryPreview === 'function') {
      window.refreshGalleryPreview(true);
    }
  };

  // ========== Shape Detection ==========

  DesignRadioSelector.prototype.normalizeShapeValue = function(value) {
    if (!value) return null;
    var normalized = value.toLowerCase().trim();
    return SHAPE_NORMALIZATION[normalized] || null;
  };

  DesignRadioSelector.prototype.detectCurrentShape = function() {
    var self = this;
    var detectedShape = null;

    var optionGroups = document.querySelectorAll('.option-group, [data-option-name]');

    optionGroups.forEach(function(group) {
      if (detectedShape) return;

      var optionName = group.dataset.optionName || '';
      optionName = optionName.toLowerCase().trim();

      if (!optionName.includes('shape') && !optionName.includes('size')) return;

      var selectedValue = null;

      var select = group.querySelector('select');
      if (select && select.value) selectedValue = select.value;

      if (!selectedValue) {
        var checkedRadio = group.querySelector('input[type="radio"]:checked');
        if (checkedRadio) selectedValue = checkedRadio.value;
      }

      if (!selectedValue) {
        var activeButton = group.querySelector('.active, [aria-checked="true"]');
        if (activeButton) selectedValue = activeButton.dataset.value || activeButton.textContent;
      }

      if (selectedValue) {
        detectedShape = self.normalizeShapeValue(selectedValue);
      }
    });

    return detectedShape;
  };

  DesignRadioSelector.prototype.detectAndFilterByShape = function() {
    var newShape = this.detectCurrentShape();

    if (newShape !== this.currentShape) {
      var previousShape = this.currentShape;
      this.currentShape = newShape;

      log('Shape changed:', previousShape, '→', newShape);

      if (previousShape !== null) {
        // v4.2.1: Save text values before clearing (persist across shape changes)
        var savedTextValues = this.preserveTextValues();

        // v4.1.4: Expand grids before clearing selection to reset collapsed state
        this.expandGrid('front');
        this.expandGrid('back');
        this.clearSelection();

        // v4.2.1: Restore text values after clearing
        this.restoreTextValues(savedTextValues);
      }

      this.filterTemplatesByShape(newShape);

      // v4.7.2: Re-run auto-selection for split child products after shape change
      // The design may exist in multiple shapes, so try to select it for the new shape
      if (previousShape !== null && this.config.isSplitChild && this.config.preSelectedDesign) {
        var self = this;
        // Small delay to let filtering complete
        setTimeout(function() {
          log('v4.7.2: Re-running auto-selection after shape change');
          self.autoSelectDesignForSplitProduct();
        }, 100);
      }
    }
  };

  /**
   * v4.2.1: Preserve text values before shape change clears them
   * @returns {Object} Saved text values for front and back
   */
  DesignRadioSelector.prototype.preserveTextValues = function() {
    var saved = { front: null, back: null };

    ['front', 'back'].forEach(function(side) {
      var textInput = document.getElementById('text-field-0-' + side);
      if (textInput && textInput.value.trim()) {
        saved[side] = textInput.value.trim();
      }
    });

    log('v4.2.1: Preserved text values before shape change:', saved);
    return saved;
  };

  /**
   * v4.2.1: Restore text values after shape change
   * @param {Object} savedValues - Text values to restore
   */
  DesignRadioSelector.prototype.restoreTextValues = function(savedValues) {
    var self = this;
    if (!savedValues) return;

    ['front', 'back'].forEach(function(side) {
      if (savedValues[side]) {
        var textInput = document.getElementById('text-field-0-' + side);
        if (textInput) {
          textInput.value = savedValues[side];
          self.sideCustomizations[side].textValue = savedValues[side];

          // Also ensure the toggle is checked and section is visible
          var textCheckbox = document.getElementById('toggle-website-text-' + side);
          if (textCheckbox) {
            textCheckbox.checked = true;
            self.sideCustomizations[side].textToggleEnabled = true;
            self.toggleFieldVisibility('text-customizer-section-' + side, true);
          }

          // Update character count
          var field = textInput.closest('.text-customizer__field');
          if (field) {
            var currentCount = field.querySelector('.text-customizer__char-current');
            if (currentCount) {
              currentCount.textContent = savedValues[side].length;
            }
          }
        }
      }
    });

    log('v4.2.1: Restored text values after shape change:', savedValues);
  };

  DesignRadioSelector.prototype.filterTemplatesByShape = function(shape) {
    var allOptions = this.container.querySelectorAll('.design-radio-selector__option');
    var visibleCount = 0;

    allOptions.forEach(function(option) {
      var radio = option.querySelector('input[type="radio"]');
      if (!radio) return;

      var templateShape = radio.dataset.shape || null;
      var shouldShow = !shape || !templateShape || templateShape === shape;

      if (shouldShow) {
        option.style.display = '';
        visibleCount++;
      } else {
        option.style.display = 'none';
        if (radio.checked) radio.checked = false;
      }
    });

    log('Filtered templates by shape "' + shape + '":', visibleCount, 'visible');

    if (visibleCount === 0 && shape) {
      this.setStatus('info', 'No designs available for ' + shape + ' shape');
    }
  };

  // ========== Double-Sided Detection ==========

  DesignRadioSelector.prototype.checkIfDoubleSidedActive = function() {
    var backSection = document.getElementById('design-radio-back');
    if (!backSection) return false;

    var isVisible = backSection.style.display !== 'none' &&
                    window.getComputedStyle(backSection).display !== 'none';

    return isVisible;
  };

  DesignRadioSelector.prototype.setupVariantChangeListener = function() {
    var self = this;

    // v4.4.2: Prevent duplicate listener registration
    if (this._variantListenersSetup) {
      return;
    }
    this._variantListenersSetup = true;

    document.addEventListener('change', function(e) {
      if (e.target.matches('select[name*="option"], input[type="radio"][name*="option"]')) {
        setTimeout(function() {
          self.checkDoubleSidedVariant();
          self.detectAndFilterByShape();
        }, 100);
      }
    });

    // v4.4.2: Listen for both variant:change and variant:changed for compatibility
    document.addEventListener('variant:change', function() {
      log('v4.4.2: variant:change event received');
      self.checkDoubleSidedVariant();
      self.detectAndFilterByShape();
    });

    document.addEventListener('variant:changed', function() {
      log('v4.4.2: variant:changed event received');
      self.checkDoubleSidedVariant();
      self.detectAndFilterByShape();
    });

    // v4.4.2: Listen for popular-variant-applied event from card-variants
    // This ensures we update even if the select value didn't change
    document.addEventListener('popular-variant-applied', function() {
      log('v4.4.2: Popular variant applied, re-checking double-sided state');
      self.checkDoubleSidedVariant();
      self.detectAndFilterByShape();
    });

    if (window.PitchPrintStateManager) {
      window.PitchPrintStateManager.on('variant:changed', function() {
        self.checkDoubleSidedVariant();
        self.detectAndFilterByShape();
      });
    }

    document.addEventListener('click', function(e) {
      var cardElement = e.target.closest('.card-p-style, [data-option-value]');
      if (cardElement) {
        setTimeout(function() {
          self.checkDoubleSidedVariant();
          self.detectAndFilterByShape();
        }, 150);
      }
    });

    log('v4.4.2: Variant change listeners setup complete');
  };

  DesignRadioSelector.prototype.checkDoubleSidedVariant = function() {
    var backSection = document.getElementById('design-radio-back');
    if (!backSection) return;

    var sidesOptionIndex = this.config.sidesOptionIndex;
    if (sidesOptionIndex === null || sidesOptionIndex === undefined) return;

    var isDoubleSided = false;

    // Check various selector patterns
    var selectOption = document.querySelector('select[data-index="option' + (sidesOptionIndex + 1) + '"]');
    if (selectOption) {
      isDoubleSided = selectOption.value.toLowerCase().includes('double');
    }

    if (!isDoubleSided) {
      var checkedRadio = document.querySelector('input[type="radio"][name="option' + sidesOptionIndex + '"]:checked');
      if (checkedRadio) {
        isDoubleSided = checkedRadio.value.toLowerCase().includes('double');
      }
    }

    if (!isDoubleSided) {
      var sidesCards = document.querySelectorAll('.card-p-style.active[data-option-name*="ide" i]');
      sidesCards.forEach(function(card) {
        if (isDoubleSided) return;
        var cardValue = card.dataset.value || '';
        if (cardValue.toLowerCase().includes('double')) {
          isDoubleSided = true;
        }
      });
    }

    if (!isDoubleSided) {
      var sidesGroup = document.querySelector('.option-group[data-option-name*="ide" i]');
      if (sidesGroup) {
        var activeInGroup = sidesGroup.querySelector('.card-p-style.active');
        if (activeInGroup) {
          var groupValue = activeInGroup.dataset.value || '';
          isDoubleSided = groupValue.toLowerCase().includes('double');
        }
      }
    }

    // v4.2.0: Notify StateManager of double-sided status
    if (window.PitchPrintStateManager && window.PitchPrintStateManager.setQuickDesignDoubleSided) {
      window.PitchPrintStateManager.setQuickDesignDoubleSided(isDoubleSided);
    }

    if (isDoubleSided) {
      this.container.classList.add('is-double-sided');
      // v4.1.5: Only show back section if front design is already selected
      // This creates a progressive disclosure flow: select front first, then back appears
      if (this.frontSelection) {
        backSection.style.display = '';
        log('Back section shown (front already selected)');
      } else {
        backSection.style.display = 'none';
        log('Back section hidden (waiting for front selection)');
      }
    } else {
      backSection.style.display = 'none';
      this.container.classList.remove('is-double-sided');
      this.clearBackSelection();
      // v4.7.1: Also update isDoubleSidedMode to prevent state mismatch
      // This fixes bug where auto-selected design won't load if variant changes from double to single
      this.isDoubleSidedMode = false;
    }
  };

  // ========== Clear Selection ==========

  DesignRadioSelector.prototype.handleSameAsFrontChange = function(checked) {
    var backGrid = document.querySelector('#design-radio-back .design-radio-selector__grid');
    if (!backGrid) return;

    if (checked) {
      backGrid.style.opacity = '0.5';
      backGrid.style.pointerEvents = 'none';

      var frontSelected = this.container.querySelector('input[name="design_template_front"]:checked');
      if (frontSelected) {
        var backRadio = this.container.querySelector(
          'input[name="design_template_back"][value="' + frontSelected.value + '"]'
        );
        if (backRadio) {
          backRadio.checked = true;

          // v4.1.4: If back grid is already collapsed, expand then re-collapse to update visual
          var wasCollapsed = backGrid.classList.contains('is-collapsed');
          if (wasCollapsed) {
            this.expandGrid('back');
          }

          this.handleTemplateSelection(backRadio, 'back');

          // v4.1.4: Re-apply dimming after collapse animation
          if (wasCollapsed) {
            setTimeout(function() {
              backGrid.style.opacity = '0.5';
              backGrid.style.pointerEvents = 'none';
            }, 600);
          }
        }
      }
    } else {
      backGrid.style.opacity = '';
      backGrid.style.pointerEvents = '';
    }
  };

  DesignRadioSelector.prototype.clearBackSelection = function() {
    var backRadios = this.container.querySelectorAll('input[name="design_template_back"]');
    backRadios.forEach(function(radio) {
      radio.checked = false;
    });

    var backInput = document.getElementById('design-radio-back-selection');
    if (backInput) backInput.value = '';

    this.backSelection = null;

    // v4.2.0: Notify StateManager of back selection cleared
    if (window.PitchPrintStateManager && window.PitchPrintStateManager.setQuickDesignBackSelected) {
      window.PitchPrintStateManager.setQuickDesignBackSelected(false);
    }
  };

  DesignRadioSelector.prototype.clearSelection = function() {
    var radios = this.container.querySelectorAll(
      'input[type="radio"][name="design_template_front"], ' +
      'input[type="radio"][name="design_template_back"]'
    );
    radios.forEach(function(radio) {
      radio.checked = false;
    });

    this.updateFormFields('', '');

    var frontInput = document.getElementById('design-radio-front-selection');
    var backInput = document.getElementById('design-radio-back-selection');
    if (frontInput) frontInput.value = '';
    if (backInput) backInput.value = '';

    var sameAsFrontCheckbox = document.getElementById('design-radio-same-as-front');
    if (sameAsFrontCheckbox) {
      sameAsFrontCheckbox.checked = false;
      this.handleSameAsFrontChange(false);
    }

    this.selectedTemplate = null;
    this.frontSelection = null;
    this.backSelection = null;
    this.previewLoaded = false;

    // v4.2.0: Notify StateManager of selections cleared
    if (window.PitchPrintStateManager) {
      if (window.PitchPrintStateManager.setQuickDesignFrontSelected) {
        window.PitchPrintStateManager.setQuickDesignFrontSelected(false);
      }
      if (window.PitchPrintStateManager.setQuickDesignBackSelected) {
        window.PitchPrintStateManager.setQuickDesignBackSelected(false);
      }
    }

    // Hide Preview button when selections are cleared
    var actionsContainer = document.querySelector('.design-radio-selector__actions');
    if (actionsContainer) {
      actionsContainer.style.display = 'none';
    }

    // v3.15: Reset customizations and hide panel
    this.resetCustomizations();
    this.hideCustomizationPanel();

    var frontStatus = document.getElementById('design-radio-status-front');
    var backStatus = document.getElementById('design-radio-status-back');
    this.setStatusOnElement(frontStatus || this.statusEl, 'ready', 'Select a design to continue');
    if (backStatus) {
      this.setStatusOnElement(backStatus, 'ready', 'Select a back design');
    }
  };

  // ========== Custom Designer ==========

  DesignRadioSelector.prototype.openCustomDesigner = function() {
    log('Opening custom designer');
    this.clearSelection();

    // v4.0: Switch to Custom Design path via StateManager if design path toggle is active
    if (window.PitchPrintStateManager && window.PitchPrintStateManager.setDesignPath) {
      log('Switching to Custom Design path via StateManager');
      window.PitchPrintStateManager.setDesignPath('custom');

      // Also trigger the toggle UI update if DesignPathToggle is available
      if (window.DesignPathToggle && window.DesignPathToggle.setPath) {
        window.DesignPathToggle.setPath('custom');
      }
      return;
    }

    // Fallback: Direct PitchPrint open (for legacy mode without design path toggle)
    var pitchprintBtn = document.querySelector('#pp_customize_design_btn') ||
                        document.querySelector('.pp-btn');

    if (pitchprintBtn) {
      pitchprintBtn.click();
    } else if (window.ppclient && window.ppclient.fire) {
      window.ppclient.fire('show-designer');
    }
  };

  // ========== Status ==========

  DesignRadioSelector.prototype.setStatus = function(state, message) {
    this.setStatusOnElement(this.statusEl, state, message);
  };

  DesignRadioSelector.prototype.setStatusOnElement = function(statusEl, state, message) {
    if (!statusEl) return;

    statusEl.className = 'design-radio-selector__status';

    if (state === 'loading') {
      statusEl.classList.add('status--loading');
    } else if (state === 'ready') {
      statusEl.classList.add('status--ready');
    } else if (state === 'error') {
      statusEl.classList.add('status--error');
    }

    var textEl = statusEl.querySelector('.status-text');
    if (textEl) {
      textEl.textContent = message;
    } else {
      statusEl.textContent = message;
    }
  };

  DesignRadioSelector.prototype.setLoadingState = function(radio, isLoading) {
    this.isLoading = isLoading;

    var option = radio.closest('.design-radio-selector__option');
    if (option) {
      if (isLoading) {
        option.classList.add('is-loading');
      } else {
        option.classList.remove('is-loading');
      }
    }

    if (isLoading) {
      this.container.classList.add('is-loading');
    } else {
      this.container.classList.remove('is-loading');
    }
  };

  // ========== Init ==========

  function initDesignRadioSelector() {
    if (!document.getElementById('design-radio-selector')) {
      return;
    }

    // Check if this is v3.14
    var container = document.getElementById('design-radio-selector');
    if (container && container.dataset.version !== '3.14') {
      log('Not v3.14, skipping (found version: ' + container.dataset.version + ')');
      return;
    }

    if (window._designRadioSelectorInstance) {
      return;
    }

    window._designRadioSelectorInstance = new DesignRadioSelector();
  }

  // ========== v4.1.0: Deck Collapse System ==========

  /**
   * Collapse the design grid into a deck view after selection
   * @param {string} side - 'front' or 'back'
   */
  DesignRadioSelector.prototype.collapseGrid = function(side) {
    var self = this;
    side = side || 'front';

    var gridSelector = side === 'back'
      ? '#design-radio-back .design-radio-selector__grid'
      : '#design-radio-front .design-radio-selector__grid';
    var grid = document.querySelector(gridSelector);

    if (!grid || grid.classList.contains('is-collapsed')) return;

    var selection = side === 'back' ? this.backSelection : this.frontSelection;
    if (!selection) return;

    log('v4.1: Collapsing grid for side:', side);

    // Mark the selected option and set deck ordering
    var options = Array.from(grid.querySelectorAll('.design-radio-selector__option'));
    var selectedIndex = -1;

    // Find selected index and mark selected
    options.forEach(function(opt, index) {
      var radio = opt.querySelector('input[type="radio"]');
      if (radio && radio.checked) {
        opt.classList.add('is-selected');
        opt.style.order = '0'; // Selected card first
        selectedIndex = index;
      } else {
        opt.classList.remove('is-selected');
      }
    });

    // Order non-selected cards after the selected one
    var deckIndex = 0;
    options.forEach(function(opt, index) {
      if (index !== selectedIndex) {
        opt.style.order = String(deckIndex + 1);
        opt.setAttribute('data-deck-index', String(deckIndex));
        deckIndex++;
      } else {
        opt.removeAttribute('data-deck-index');
      }
    });

    // Collapse the grid
    grid.classList.add('is-collapsed');

    // Store reference to click handler for removal
    var expandHandler = function(e) {
      // Don't expand if clicking the selected card
      if (e.target.closest('.is-selected')) {
        return;
      }
      self.expandGrid(side);
    };

    grid._expandHandler = expandHandler;
    grid.addEventListener('click', expandHandler);

    // Auto-scroll to keep design selector in view
    // v4.7.0: Skip scroll for auto-selected split child products (counter handles both sides)
    if (this._skipScrollCount > 0) {
      this._skipScrollCount--;
    } else {
      this.scrollToSelector(side);
    }
  };

  /**
   * Expand the grid back to full view
   * @param {string} side - 'front' or 'back'
   */
  DesignRadioSelector.prototype.expandGrid = function(side) {
    side = side || 'front';

    var gridSelector = side === 'back'
      ? '#design-radio-back .design-radio-selector__grid'
      : '#design-radio-front .design-radio-selector__grid';
    var grid = document.querySelector(gridSelector);

    if (!grid || !grid.classList.contains('is-collapsed')) return;

    log('v4.1: Expanding grid for side:', side);

    // Remove collapse class
    grid.classList.remove('is-collapsed');
    grid.classList.add('is-expanding');

    // Clean up order styles and data attributes from collapsed state
    var options = grid.querySelectorAll('.design-radio-selector__option');
    options.forEach(function(opt) {
      opt.style.order = '';
      opt.removeAttribute('data-deck-index');
    });

    // Remove click handler
    if (grid._expandHandler) {
      grid.removeEventListener('click', grid._expandHandler);
      grid._expandHandler = null;
    }

    // Clean up after animation
    setTimeout(function() {
      grid.classList.remove('is-expanding');
    }, 300);
  };

  /**
   * Scroll viewport to the design radio selector container
   * v4.5.3: Position container at 15% from top of viewport instead of at the very top
   * @param {string} side - 'front' or 'back'
   */
  DesignRadioSelector.prototype.scrollToSelector = function(side) {
    side = side || 'front';
    var selectorId = side === 'back' ? 'design-radio-back' : 'design-radio-front';
    var selector = document.getElementById(selectorId);

    if (selector) {
      setTimeout(function() {
        var rect = selector.getBoundingClientRect();
        var viewportHeight = window.innerHeight;
        var offsetFromTop = viewportHeight * 0.15; // 15% from top of viewport
        var scrollTarget = window.pageYOffset + rect.top - offsetFromTop;

        window.scrollTo({
          top: scrollTarget,
          behavior: 'smooth'
        });
      }, 350); // After collapse animation completes
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDesignRadioSelector);
  } else {
    initDesignRadioSelector();
  }

  window.DesignRadioSelectorV314 = DesignRadioSelector;

  log('Design Radio Selector v4.7.2 (Split Child Shape Change Fix) script loaded');

})();
